import * as PIXI from 'pixi.js';
import type { SpellAreaEffect, EffectName } from '@machina/core';
import { EFFECT_COLORS } from '@machina/core';
import { useEffectStore } from '../stores/effectStore';
import { setPixiEffectClicked } from './pixiTokenState';
import { getFlameSkin } from './fireShader';
import { useMapStore } from '../stores/mapStore';

/**
 * EffectsLayer
 *
 * Renders persistent spell area effects on the battle map with
 * type-specific themed particles. Fire uses a GLSL fragment shader
 * with domain-warped fBm noise (Inigo Quilez technique) for organic
 * volumetric flames, plus sparse ember particles for secondary detail.
 * Thunder uses branching lightning bolts with direction and length variance.
 *
 * Click an effect to select it (gold highlight), drag to move
 * (grid-snapped, broadcast to all players), Delete key to remove.
 *
 * MVC: SpellAreaEffect + EffectBehavior (in @machina/core) = Model.
 * This file = View (Pixi.js 2D). Future renderers (Unreal/Niagara,
 * Three.js) consume the same model with their own particle systems.
 *
 * Sits between TokenLayer (zIndex 3) and PingEffectLayer (zIndex 999).
 */

interface ActiveAreaEffect {
  effect: SpellAreaEffect;
  container: PIXI.Container;
  graphics: PIXI.Graphics;
  hitArea: PIXI.Graphics;
  particles: EffectParticle[];
  elapsed: number;
  /** fBm fire shader mesh (fire effects only) */
  fireMesh?: PIXI.Mesh;
  /** Reference to fire shader for uniform updates */
  fireShader?: PIXI.Shader;
  /** fBm blizzard shader mesh (ice effects only) */
  iceMesh?: PIXI.Mesh;
  /** Reference to blizzard shader for uniform updates */
  iceShader?: PIXI.Shader;
  /** fBm decay shader mesh (decay effects only) */
  decayMesh?: PIXI.Mesh;
  /** Reference to decay shader for uniform updates */
  decayShader?: PIXI.Shader;
  /** fBm fey shader mesh (fey effects only) */
  feyMesh?: PIXI.Mesh;
  /** Reference to fey shader for uniform updates */
  feyShader?: PIXI.Shader;
  /** Darkness shader mesh (darkness effects only) */
  darkMesh?: PIXI.Mesh;
  /** Reference to darkness shader for uniform updates */
  darkShader?: PIXI.Shader;
}

interface EffectParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: number;
  alpha: number;
  rotation: number;
  rotSpeed: number;
  /** Type-specific variant (0-5) for visual diversity */
  variant: number;
  /** Per-particle random seed for noise-based turbulence */
  seed: number;
  /** Layer: 0=inner/core, 1=mid, 2=outer — used by fire for heat gradient */
  layer: number;
}

const PARTICLE_COUNT: Record<EffectName, number> = {
  fire: 18,  // embers only — shader handles the main fire field
  ice: 50,  // blizzard particles — shader handles the main fog/whiteout field
  thunder: 12,
  divine: 30,
  decay: 18,  // drips only — shader handles the main ooze field
  eldritch: 35,
  psychic: 28,
  menace: 32,
  fey: 14,  // sparkle motes — shader handles the main fairy lights
  darkness: 8,  // minimal wisps — darkness is mostly a solid overlay
};

// ─── Cheap noise helper (no dependencies) ──────────────────
// Sine-based pseudo-noise: not true simplex, but fast and organic enough
// for particle turbulence at 60fps. Two octaves for layered look.
function turbulence2(x: number, y: number, t: number, seed: number): number {
  const s = seed * 17.31;
  return (
    Math.sin(x * 1.2 + t * 2.1 + s) * 0.5 +
    Math.sin(y * 0.9 - t * 1.7 + s * 0.7) * 0.3 +
    Math.sin((x + y) * 2.3 + t * 3.3 + s * 1.3) * 0.2
  );
}

// ─── Fire Shader (GLSL) ──────────────────────────────────────
// Domain-warped fBm volumetric fire inspired by THREE.Fire and
// Inigo Quilez's "Warping" article. Double-warped noise creates
// organic flowing patterns; upward scroll + fire color ramp give
// convincing top-down flame effect. Additive blending for glow.

const FIRE_VERT = `
attribute vec2 aVertexPosition;
attribute vec2 aUvs;
uniform mat3 translationMatrix;
uniform mat3 projectionMatrix;
varying vec2 vUvs;

void main() {
    gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
    vUvs = aUvs;
}
`;

const FIRE_FRAG = `
precision highp float;
varying vec2 vUvs;

uniform float uTime;
uniform float uIntensity;
uniform vec2 uOriginUV;   // fire center in UV space
uniform vec2 uScale;      // UV-to-radius scale factor
uniform float uShape;     // 0=circle, 1=square, 2=cone, 3=line
uniform float uLineHalf;  // half-width of line shape in radius units

// Skinnable color ramp
uniform vec3 uRamp0; // black
uniform vec3 uRamp1; // ember
uniform vec3 uRamp2; // red
uniform vec3 uRamp3; // orange
uniform vec3 uRamp4; // yellow
uniform vec3 uRamp5; // white

// ─── Hash-based value noise ───────────────────────────────
float hash(vec2 p) {
    p = fract(p * vec2(443.897, 441.423));
    p += dot(p, p.yx + 19.19);
    return fract((p.x + p.y) * p.x);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}

// ─── fBm with octave rotation to break grid artifacts ─────
float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    // Rotate between octaves — eliminates axis-aligned banding
    mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
    for (int i = 0; i < 5; i++) {
        sum += amp * noise(p);
        p = rot * p * 2.0;   // lacunarity = 2.0
        amp *= 0.5;           // gain = 0.5
    }
    return sum;
}

// ─── Skinnable fire color ramp ───────────────────────────
vec3 fireColor(float t) {
    if (t < 0.15) return mix(uRamp0, uRamp1, t / 0.15);
    if (t < 0.30) return mix(uRamp1, uRamp2, (t - 0.15) / 0.15);
    if (t < 0.50) return mix(uRamp2, uRamp3, (t - 0.30) / 0.20);
    if (t < 0.70) return mix(uRamp3, uRamp4, (t - 0.50) / 0.20);
    return mix(uRamp4, uRamp5, clamp((t - 0.70) / 0.30, 0.0, 1.0));
}

void main() {
    // Map UV to fire-local coordinates (centered on origin, in radius units)
    vec2 local = (vUvs - uOriginUV) * uScale;

    // Shape mask — soft edge fade
    float dist = length(local);
    float mask;
    if (uShape < 0.5) {
        // Circle
        mask = 1.0 - smoothstep(0.75, 1.05, dist);
    } else if (uShape < 1.5) {
        // Square
        vec2 ap = abs(local);
        float sq = max(ap.x, ap.y);
        mask = 1.0 - smoothstep(0.75, 1.05, sq);
    } else if (uShape < 2.5) {
        // Cone: 60-degree wedge from origin, extending in +x direction
        // local is in radius units, origin at (0,0)
        float angle = atan(local.y, local.x);
        float coneHalf = 0.5236; // pi/6 = 30 degrees half-angle
        float angleFade = 1.0 - smoothstep(coneHalf * 0.8, coneHalf * 1.1, abs(angle));
        float distFade = 1.0 - smoothstep(0.75, 1.05, dist);
        // Origin tip fade — soften the very center
        float tipFade = smoothstep(0.0, 0.12, dist);
        mask = angleFade * distFade * tipFade;
    } else {
        // Line: rectangle extending in +x direction
        // local.x: 0 to 1 is the length, local.y: +/- uLineHalf is the width
        float xFade = (1.0 - smoothstep(0.75, 1.05, local.x)) * smoothstep(-0.05, 0.1, local.x);
        float yFade = 1.0 - smoothstep(uLineHalf * 0.7, uLineHalf * 1.1, abs(local.y));
        mask = xFade * yFade;
    }
    if (mask < 0.001) { gl_FragColor = vec4(0.0); return; }

    // ─── Domain-warped fBm (Inigo Quilez technique) ──────
    // Noise coordinates: stretch vertically for flame shape, scroll upward
    vec2 p = local * vec2(1.5, 2.0);

    // First warp layer — large-scale distortion
    vec2 q = vec2(
        fbm(p + vec2(0.0, -uTime * 0.55)),
        fbm(p + vec2(5.2, -uTime * 0.65))
    );

    // Second warp layer — warp the warp for deep organic structure
    vec2 r = vec2(
        fbm(p + 4.0 * q + vec2(1.7, -uTime * 0.35)),
        fbm(p + 4.0 * q + vec2(8.3, -uTime * 0.45))
    );

    // Final fire noise with strong upward scroll
    float n = fbm(p + 3.5 * r + vec2(0.0, -uTime * 1.1));

    // ─── Spatial intensity shaping ───────────────────────
    // Center boost: hotter toward the middle
    float centerHeat = 1.0 - dist * 0.35;

    // Slight directional bias: marginally more intensity at bottom
    // (local.y > 0 = below origin on screen = fire base)
    float dirBias = 1.0 + local.y * 0.12;

    // Subtle whole-field pulse
    float pulse = 0.92 + 0.08 * sin(uTime * 2.3);

    // Combine
    float fireIntensity = n * centerHeat * dirBias * pulse * 1.35;
    fireIntensity = clamp(fireIntensity, 0.0, 1.0);

    // ─── Output ──────────────────────────────────────────
    vec3 color = fireColor(fireIntensity);
    float alpha = smoothstep(0.06, 0.28, fireIntensity) * mask * uIntensity;

    // Slight brightness boost for vibrancy
    color *= 1.15;

    // Premultiplied alpha output (for ADD blending)
    gl_FragColor = vec4(color * alpha, alpha);
}
`;

// ─── Blizzard Shader (GLSL) ──────────────────────────────────
// Domain-warped fBm blizzard: horizontal wind-driven fog with
// swirling snow bands. Same fBm technique as fire but scrolling
// sideways with icy color ramp. NORMAL blending for obscuring fog.

const ICE_VERT = FIRE_VERT; // Same vertex shader

const ICE_FRAG = `
precision highp float;
varying vec2 vUvs;

uniform float uTime;
uniform float uIntensity;
uniform vec2 uOriginUV;
uniform vec2 uScale;
uniform float uShape;
uniform float uLineHalf;

// ─── Hash-based value noise ───────────────────────────────
float hash(vec2 p) {
    p = fract(p * vec2(443.897, 441.423));
    p += dot(p, p.yx + 19.19);
    return fract((p.x + p.y) * p.x);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}

// ─── fBm — standard multi-octave noise ───────────────────
float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
    for (int i = 0; i < 5; i++) {
        sum += amp * noise(p);
        p = rot * p * 2.0;
        amp *= 0.5;
    }
    return sum;
}

// ─── Billowy fBm — abs(noise) creates puffy cloud shapes ─
// Each octave uses |noise - 0.5| which creates ridged edges
// and puffy cloud-like shapes instead of smooth gradients
float fbmBillowy(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
    for (int i = 0; i < 5; i++) {
        // abs(noise*2-1) creates sharp ridges → billowy clouds
        float n = abs(noise(p) * 2.0 - 1.0);
        // Invert: 1-n makes peaks (bright) where clouds are
        sum += amp * (1.0 - n);
        p = rot * p * 2.0;
        amp *= 0.5;
    }
    return sum;
}

// ─── Blizzard color ramp ─────────────────────────────────
vec3 iceColor(float t) {
    vec3 clear  = vec3(0.72, 0.82, 0.92);
    vec3 pale   = vec3(0.8, 0.88, 0.96);
    vec3 frost  = vec3(0.88, 0.93, 0.99);
    vec3 snow   = vec3(0.95, 0.97, 1.0);
    vec3 bright = vec3(1.0, 1.0, 1.0);

    if (t < 0.25) return mix(clear, pale, t / 0.25);
    if (t < 0.5)  return mix(pale, frost, (t - 0.25) / 0.25);
    if (t < 0.75) return mix(frost, snow, (t - 0.5) / 0.25);
    return mix(snow, bright, clamp((t - 0.75) / 0.25, 0.0, 1.0));
}

void main() {
    vec2 local = (vUvs - uOriginUV) * uScale;

    // Shape mask — soft edge fade
    float dist = length(local);
    float mask;
    if (uShape < 0.5) {
        mask = 1.0 - smoothstep(0.75, 1.05, dist);
    } else if (uShape < 1.5) {
        vec2 ap = abs(local);
        float sq = max(ap.x, ap.y);
        mask = 1.0 - smoothstep(0.75, 1.05, sq);
    } else if (uShape < 2.5) {
        float angle = atan(local.y, local.x);
        float coneHalf = 0.5236;
        float angleFade = 1.0 - smoothstep(coneHalf * 0.8, coneHalf * 1.1, abs(angle));
        float distFade = 1.0 - smoothstep(0.75, 1.05, dist);
        float tipFade = smoothstep(0.0, 0.12, dist);
        mask = angleFade * distFade * tipFade;
    } else {
        float xFade = (1.0 - smoothstep(0.75, 1.05, local.x)) * smoothstep(-0.05, 0.1, local.x);
        float yFade = 1.0 - smoothstep(uLineHalf * 0.7, uLineHalf * 1.1, abs(local.y));
        mask = xFade * yFade;
    }
    if (mask < 0.001) { gl_FragColor = vec4(0.0); return; }

    // ─── Layered billowy blizzard ────────────────────────
    // Instead of deep domain warping (which looks liquid), we layer
    // independent billowy noise fields at different scales/speeds.
    // This creates distinct puffy cloud masses blowing past each other.

    // Layer 1: Large slow-moving cloud masses (background fog)
    vec2 p1 = local * vec2(1.8, 1.2) + vec2(-uTime * 0.3, uTime * 0.05);
    float cloud1 = fbmBillowy(p1);

    // Layer 2: Medium wind-driven bands (main blizzard body)
    // Stretched horizontally for wind-driven appearance
    vec2 p2 = local * vec2(3.0, 1.5) + vec2(-uTime * 0.8, uTime * 0.12);
    float cloud2 = fbmBillowy(p2 + vec2(3.7, 1.2));

    // Layer 3: Fast small gusts (surface detail)
    vec2 p3 = local * vec2(5.0, 2.5) + vec2(-uTime * 1.5, -uTime * 0.2);
    float cloud3 = fbmBillowy(p3 + vec2(7.1, 4.3));

    // Light single-warp to add gentle organic drift (NOT deep warp)
    float warpStrength = 0.15;
    vec2 warp = vec2(
        fbm(local * 1.5 + vec2(uTime * 0.1, 0.0)),
        fbm(local * 1.5 + vec2(0.0, uTime * 0.08))
    ) * warpStrength;

    // Re-sample layer 2 with gentle warp for organic shape
    float cloud2w = fbmBillowy(p2 + warp * 3.0 + vec2(3.7, 1.2));

    // Combine layers with different weights
    // Layer 1: base fog, layer 2: main clouds, layer 3: gusting detail
    float combined = cloud1 * 0.3 + cloud2w * 0.5 + cloud3 * 0.2;

    // ─── Sharpen into distinct cloud shapes ──────────────
    // Threshold to create defined edges instead of smooth gradients
    float sharpened = smoothstep(0.28, 0.55, combined);

    // Add back some mid-tone for depth (not just on/off)
    float midtone = smoothstep(0.2, 0.65, combined) * 0.4;
    float blizzardIntensity = max(sharpened, midtone);

    // ─── Spatial shaping ─────────────────────────────────
    // Center density: slightly thicker toward middle
    float centerDensity = 1.0 - dist * 0.15;

    // Wind gusting — periodic surges create blizzard pulses
    float gust = 0.8 + 0.2 * sin(uTime * 1.8 + local.x * 2.0);

    blizzardIntensity *= centerDensity * gust;
    blizzardIntensity = clamp(blizzardIntensity, 0.0, 1.0);

    // ─── High-frequency edge breakup ─────────────────────
    // Add fine noise to fragment cloud edges (prevents smooth blobs)
    float edgeNoise = noise(local * 12.0 + vec2(-uTime * 2.0, uTime * 0.5));
    // Only affects edges (where intensity is mid-range)
    float edgeMask = 1.0 - abs(blizzardIntensity * 2.0 - 1.0);
    blizzardIntensity += (edgeNoise - 0.5) * edgeMask * 0.25;
    blizzardIntensity = clamp(blizzardIntensity, 0.0, 1.0);

    // ─── Output ──────────────────────────────────────────
    vec3 color = iceColor(blizzardIntensity);
    float alpha = blizzardIntensity * mask * uIntensity;

    gl_FragColor = vec4(color * alpha, alpha);
}
`;

// ─── Decay Shader (GLSL) ─────────────────────────────────────
// Deep domain-warped fBm with slow viscous motion. The liquid look
// that was wrong for blizzard is exactly right for necrotic ooze.
// Dark green color ramp, downward flow, bubbling surface texture.

const DECAY_VERT = FIRE_VERT;

const DECAY_FRAG = `
precision highp float;
varying vec2 vUvs;

uniform float uTime;
uniform float uIntensity;
uniform vec2 uOriginUV;
uniform vec2 uScale;
uniform float uShape;
uniform float uLineHalf;

float hash(vec2 p) {
    p = fract(p * vec2(443.897, 441.423));
    p += dot(p, p.yx + 19.19);
    return fract((p.x + p.y) * p.x);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}

float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
    for (int i = 0; i < 5; i++) {
        sum += amp * noise(p);
        p = rot * p * 2.0;
        amp *= 0.5;
    }
    return sum;
}

// ─── Necrotic ooze color ramp ────────────────────────────
// Near-black → dark green → sickly yellow-green → bright toxic
vec3 decayColor(float t) {
    vec3 void_   = vec3(0.02, 0.04, 0.01);
    vec3 rot     = vec3(0.08, 0.15, 0.03);
    vec3 bile    = vec3(0.18, 0.30, 0.05);
    vec3 slime   = vec3(0.30, 0.50, 0.08);
    vec3 toxic   = vec3(0.45, 0.65, 0.12);
    vec3 bright  = vec3(0.55, 0.80, 0.20);

    if (t < 0.15) return mix(void_, rot, t / 0.15);
    if (t < 0.30) return mix(rot, bile, (t - 0.15) / 0.15);
    if (t < 0.50) return mix(bile, slime, (t - 0.30) / 0.20);
    if (t < 0.75) return mix(slime, toxic, (t - 0.50) / 0.25);
    return mix(toxic, bright, clamp((t - 0.75) / 0.25, 0.0, 1.0));
}

void main() {
    vec2 local = (vUvs - uOriginUV) * uScale;

    // Shape mask
    float dist = length(local);
    float mask;
    if (uShape < 0.5) {
        mask = 1.0 - smoothstep(0.75, 1.05, dist);
    } else if (uShape < 1.5) {
        vec2 ap = abs(local);
        float sq = max(ap.x, ap.y);
        mask = 1.0 - smoothstep(0.75, 1.05, sq);
    } else if (uShape < 2.5) {
        float angle = atan(local.y, local.x);
        float coneHalf = 0.5236;
        float angleFade = 1.0 - smoothstep(coneHalf * 0.8, coneHalf * 1.1, abs(angle));
        float distFade = 1.0 - smoothstep(0.75, 1.05, dist);
        float tipFade = smoothstep(0.0, 0.12, dist);
        mask = angleFade * distFade * tipFade;
    } else {
        float xFade = (1.0 - smoothstep(0.75, 1.05, local.x)) * smoothstep(-0.05, 0.1, local.x);
        float yFade = 1.0 - smoothstep(uLineHalf * 0.7, uLineHalf * 1.1, abs(local.y));
        mask = xFade * yFade;
    }
    if (mask < 0.001) { gl_FragColor = vec4(0.0); return; }

    // ─── Deep domain-warped fBm (viscous ooze) ──────────
    // Slow time — ooze is heavy and sluggish
    float t = uTime * 0.4;

    // Noise coordinates — uniform scale, slight vertical stretch for dripping
    vec2 p = local * vec2(1.8, 2.2);

    // First warp — large viscous flow
    vec2 q = vec2(
        fbm(p + vec2(0.0, t * 0.3)),
        fbm(p + vec2(5.2, t * 0.4))
    );

    // Second warp — deep goopy distortion (high multiplier = more viscous)
    vec2 r = vec2(
        fbm(p + 5.0 * q + vec2(1.7, t * 0.2)),
        fbm(p + 5.0 * q + vec2(8.3, t * 0.25))
    );

    // Third warp — extra depth for truly gloopy organic shapes
    vec2 s = vec2(
        fbm(p + 4.0 * r + vec2(3.1, -t * 0.15)),
        fbm(p + 4.0 * r + vec2(6.7, t * 0.18))
    );

    // Final ooze noise — very deep warping for maximum viscosity
    float n = fbm(p + 3.5 * s + vec2(0.0, t * 0.5));

    // ─── Spatial shaping ─────────────────────────────────
    // Center concentration — thicker goo toward middle
    float centerMass = 1.0 - dist * 0.3;

    // Slow pulsing — goo breathes
    float pulse = 0.88 + 0.12 * sin(t * 1.5);

    // Downward bias — more visible below center (pooling)
    float poolBias = 1.0 + local.y * 0.15;

    float oozeIntensity = n * centerMass * pulse * poolBias * 1.4;
    oozeIntensity = clamp(oozeIntensity, 0.0, 1.0);

    // ─── Bubbling surface detail ─────────────────────────
    // Scattered "bubbles" — bright spots that pop in and out
    float bubbleNoise = noise(local * 8.0 + vec2(t * 0.5, -t * 0.3));
    float bubble2 = noise(local * 12.0 + vec2(-t * 0.7, t * 0.4));
    // Sharp threshold for distinct bubble shapes
    float bubbles = smoothstep(0.72, 0.78, bubbleNoise) * 0.35;
    bubbles += smoothstep(0.75, 0.80, bubble2) * 0.25;
    // Only show bubbles where there's already ooze
    oozeIntensity += bubbles * smoothstep(0.15, 0.4, oozeIntensity);
    oozeIntensity = clamp(oozeIntensity, 0.0, 1.0);

    // ─── Output ──────────────────────────────────────────
    vec3 color = decayColor(oozeIntensity);
    float alpha = smoothstep(0.05, 0.25, oozeIntensity) * mask * uIntensity;

    // Slightly boost brightness in the bright spots
    color *= 1.0 + bubbles * 0.5;

    // Premultiplied alpha (ADD blending for toxic glow)
    gl_FragColor = vec4(color * alpha, alpha);
}
`;

// ─── Fey Shader (GLSL) ──────────────────────────────────────
// Ethereal fairy lights that bloom and vanish — distinct luminous
// spots that pulse independently, like will-o'-wisps or
// bioluminescent spores in an enchanted forest. Soft pink/purple/
// gold color palette. Uses multiple noise-driven "lanterns" that
// appear and disappear on their own rhythms.

const FEY_VERT = FIRE_VERT;

const FEY_FRAG = `
precision highp float;
varying vec2 vUvs;

uniform float uTime;
uniform float uIntensity;
uniform vec2 uOriginUV;
uniform vec2 uScale;
uniform float uShape;
uniform float uLineHalf;

float hash(vec2 p) {
    p = fract(p * vec2(443.897, 441.423));
    p += dot(p, p.yx + 19.19);
    return fract((p.x + p.y) * p.x);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}

float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
    for (int i = 0; i < 4; i++) {
        sum += amp * noise(p);
        p = rot * p * 2.0;
        amp *= 0.5;
    }
    return sum;
}

// ─── Fey color ramp — warm enchanted palette ──────────────
vec3 feyColor(float t, float hueShift) {
    // Shift between pink, purple, gold, pale green
    vec3 dim    = vec3(0.15, 0.05, 0.20);  // deep purple
    vec3 pink   = vec3(0.90, 0.40, 0.65);
    vec3 orchid = vec3(0.75, 0.35, 0.90);
    vec3 gold   = vec3(1.00, 0.85, 0.40);
    vec3 mint   = vec3(0.50, 0.95, 0.70);

    // Use hueShift to pick different color paths per wisp
    float h = fract(hueShift);
    vec3 mid, bright;
    if (h < 0.33) {
        mid = pink;
        bright = gold;
    } else if (h < 0.66) {
        mid = orchid;
        bright = mint;
    } else {
        mid = gold;
        bright = pink;
    }

    if (t < 0.3) return mix(dim, mid, t / 0.3);
    if (t < 0.7) return mix(mid, bright, (t - 0.3) / 0.4);
    return mix(bright, vec3(1.0), clamp((t - 0.7) / 0.3, 0.0, 1.0));
}

void main() {
    vec2 local = (vUvs - uOriginUV) * uScale;

    // Shape mask
    float dist = length(local);
    float mask;
    if (uShape < 0.5) {
        mask = 1.0 - smoothstep(0.75, 1.05, dist);
    } else if (uShape < 1.5) {
        vec2 ap = abs(local);
        float sq = max(ap.x, ap.y);
        mask = 1.0 - smoothstep(0.75, 1.05, sq);
    } else if (uShape < 2.5) {
        float angle = atan(local.y, local.x);
        float coneHalf = 0.5236;
        float angleFade = 1.0 - smoothstep(coneHalf * 0.8, coneHalf * 1.1, abs(angle));
        float distFade = 1.0 - smoothstep(0.75, 1.05, dist);
        float tipFade = smoothstep(0.0, 0.12, dist);
        mask = angleFade * distFade * tipFade;
    } else {
        float xFade = (1.0 - smoothstep(0.75, 1.05, local.x)) * smoothstep(-0.05, 0.1, local.x);
        float yFade = 1.0 - smoothstep(uLineHalf * 0.7, uLineHalf * 1.1, abs(local.y));
        mask = xFade * yFade;
    }
    if (mask < 0.001) { gl_FragColor = vec4(0.0); return; }

    // ─── Fairy light wisps ─────────────────────────────────
    // Multiple independent "lanterns" that bloom and fade
    // Each wisp has its own position (noise-driven), timing, and color

    float totalLight = 0.0;
    vec3 totalColor = vec3(0.0);
    float t = uTime * 0.6;

    for (int i = 0; i < 7; i++) {
        float fi = float(i);
        // Each wisp has a unique phase offset and drift seed
        float phase = fi * 1.37 + 0.5;

        // Wisp center drifts slowly through the area
        vec2 center = vec2(
            sin(t * 0.3 + phase * 2.1) * 0.55 + cos(t * 0.17 + phase) * 0.25,
            cos(t * 0.25 - phase * 1.7) * 0.5 + sin(t * 0.13 + phase * 0.7) * 0.2
        );

        // Appearance cycle — each wisp blooms and fades independently
        float cycle = sin(t * (0.4 + fi * 0.12) + phase * 3.14) * 0.5 + 0.5;
        // Sharp bloom threshold: wisps pop in and linger, then fade
        float bloom = smoothstep(0.25, 0.55, cycle) * (1.0 - smoothstep(0.7, 0.95, cycle));

        // Distance from this wisp's center
        float wDist = length(local - center);
        // Wisp radius varies per wisp and over time
        float radius = 0.15 + 0.10 * sin(t * 0.5 + phase * 2.0);
        // Soft glow falloff
        float glow = exp(-wDist * wDist / (radius * radius * 2.0));

        float brightness = glow * bloom;
        vec3 wispColor = feyColor(brightness, fi * 0.31);

        totalLight += brightness;
        totalColor += wispColor * brightness;
    }

    // ─── Ambient fairy dust ────────────────────────────────
    // Very faint background sparkle
    float dust = fbm(local * 6.0 + vec2(t * 0.2, -t * 0.15));
    float dustSparkle = smoothstep(0.55, 0.7, dust) * 0.12;
    totalLight += dustSparkle;
    totalColor += vec3(0.7, 0.5, 0.9) * dustSparkle;

    totalLight = clamp(totalLight, 0.0, 1.2);

    // Normalize color
    vec3 finalColor = totalLight > 0.001 ? totalColor / totalLight : vec3(0.15, 0.05, 0.2);
    float alpha = smoothstep(0.02, 0.15, totalLight) * mask * uIntensity;

    // Slightly dim base for atmospheric feel
    finalColor = mix(vec3(0.06, 0.02, 0.08), finalColor, min(1.0, totalLight * 1.5));

    gl_FragColor = vec4(finalColor * alpha, alpha);
}
`;

// ─── Darkness Shader (GLSL) ────────────────────────────────
// Swirling void with tendrils of deeper darkness. Not pure black
// so tokens remain as visible silhouettes. Cat-slit eyes appear
// and disappear in the murk, watching. Uses slow domain-warped
// noise for organic roiling darkness, with procedural eye shapes
// placed at noise-driven positions.

const DARK_VERT = FIRE_VERT;

const DARK_FRAG = `
precision highp float;
varying vec2 vUvs;

uniform float uTime;
uniform float uIntensity;
uniform vec2 uOriginUV;
uniform vec2 uScale;
uniform float uShape;
uniform float uLineHalf;

float hash(vec2 p) {
    p = fract(p * vec2(443.897, 441.423));
    p += dot(p, p.yx + 19.19);
    return fract((p.x + p.y) * p.x);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}

float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
    for (int i = 0; i < 5; i++) {
        sum += amp * noise(p);
        p = rot * p * 2.0;
        amp *= 0.5;
    }
    return sum;
}

// ─── Cat-slit eye SDF ────────────────────────────────────
// Returns 0 inside eye, 1 outside. Almond shape with vertical slit pupil.
float catEye(vec2 p, float size) {
    // Almond eye shape: intersection of two circles
    float eyeW = size * 1.6;
    float eyeH = size * 0.7;
    // Ellipse for the overall eye shape
    vec2 scaled = p / vec2(eyeW, eyeH);
    float eyeDist = length(scaled);
    float eyeMask = 1.0 - smoothstep(0.85, 1.0, eyeDist);

    // Vertical cat-slit pupil
    float slitW = size * 0.12;
    float slitH = size * 0.55;
    vec2 slitScaled = p / vec2(slitW, slitH);
    float slitDist = length(slitScaled);
    float slit = 1.0 - smoothstep(0.7, 1.0, slitDist);

    return eyeMask - slit * 0.7; // pupil is darker (subtracted)
}

void main() {
    vec2 local = (vUvs - uOriginUV) * uScale;

    // Shape mask
    float dist = length(local);
    float mask;
    if (uShape < 0.5) {
        mask = 1.0 - smoothstep(0.75, 1.05, dist);
    } else if (uShape < 1.5) {
        vec2 ap = abs(local);
        float sq = max(ap.x, ap.y);
        mask = 1.0 - smoothstep(0.75, 1.05, sq);
    } else if (uShape < 2.5) {
        float angle = atan(local.y, local.x);
        float coneHalf = 0.5236;
        float angleFade = 1.0 - smoothstep(coneHalf * 0.8, coneHalf * 1.1, abs(angle));
        float distFade = 1.0 - smoothstep(0.75, 1.05, dist);
        float tipFade = smoothstep(0.0, 0.12, dist);
        mask = angleFade * distFade * tipFade;
    } else {
        float xFade = (1.0 - smoothstep(0.75, 1.05, local.x)) * smoothstep(-0.05, 0.1, local.x);
        float yFade = 1.0 - smoothstep(uLineHalf * 0.7, uLineHalf * 1.1, abs(local.y));
        mask = xFade * yFade;
    }
    if (mask < 0.001) { gl_FragColor = vec4(0.0); return; }

    // ─── Swirling darkness ─────────────────────────────────
    float t = uTime * 0.3; // very slow
    float rawTime = uTime;  // un-scaled for delays

    // Domain warp for organic roiling tendrils
    vec2 p = local * 2.0;
    vec2 q = vec2(
        fbm(p + vec2(0.0, t * 0.2)),
        fbm(p + vec2(5.2, -t * 0.15))
    );
    vec2 r = vec2(
        fbm(p + 4.0 * q + vec2(1.7, t * 0.1)),
        fbm(p + 4.0 * q + vec2(8.3, -t * 0.12))
    );
    float n = fbm(p + 3.0 * r);

    // Darker tendrils: low values = extra dark swirls
    float darknessBase = 0.65 + 0.12 * sin(t * 0.5);
    float swirl = n * 0.25;
    float darkness = darknessBase + swirl * (0.7 + 0.3 * sin(t * 0.8));

    // The "deeper" swirls
    float deepSwirl = smoothstep(0.3, 0.5, n) * 0.15;
    darkness -= deepSwirl;

    // ─── Darker pockets ─────────────────────────────────────
    // Extra-dark pools that drift slowly through the area
    float pocket1 = fbm(local * 3.0 + vec2(t * 0.08, -t * 0.06));
    float pocket2 = fbm(local * 2.5 + vec2(-t * 0.05, t * 0.09) + 7.7);
    float pocketMask = smoothstep(0.55, 0.7, pocket1) * smoothstep(0.5, 0.65, pocket2);
    darkness += pocketMask * 0.2; // darker pockets push opacity higher

    // A third slow-moving void pocket for variety
    float pocket3 = fbm(local * 1.8 + vec2(t * 0.04, t * 0.03) + 3.3);
    float deepPocket = smoothstep(0.6, 0.75, pocket3);
    darkness += deepPocket * 0.12;

    darkness = clamp(darkness, 0.45, 0.88);

    // ─── Cat eyes — delayed surprise ─────────────────────────
    // Eyes don't appear until 4 seconds after the effect starts.
    // Then they open slowly (iris widens), hold, then close (iris narrows).
    float eyeDelay = 4.0;
    float eyeAge = max(0.0, rawTime - eyeDelay);

    float eyeGlow = 0.0;
    if (eyeAge > 0.0) {
        // Drift position (only starts moving after eyes appear)
        float et = eyeAge * 0.3;
        vec2 eyePos = vec2(
            sin(et * 0.15) * 0.35 + cos(et * 0.11) * 0.15,
            cos(et * 0.12) * 0.3 + sin(et * 0.08) * 0.1
        );

        // Appearance cycle — eyes open, hold, close
        float cycle = sin(et * 0.2) * 0.5 + 0.5;
        float eyeVisibility = smoothstep(0.2, 0.45, cycle) * (1.0 - smoothstep(0.7, 0.9, cycle));

        // Fade near edges
        float eyeEdgeDist = length(eyePos);
        eyeVisibility *= 1.0 - smoothstep(0.45, 0.75, eyeEdgeDist);

        // Open/close animation: eye height scales with visibility
        // When opening: slit → almond (height grows from near-zero)
        // When closing: almond → slit (height shrinks back)
        float openAmount = eyeVisibility; // 0 = closed slit, 1 = fully open

        if (openAmount > 0.01) {
            float eyeSize = 0.045;
            float gap = eyeSize * 1.8;
            vec2 leftEye = local - eyePos - vec2(-gap, 0.0);
            vec2 rightEye = local - eyePos - vec2(gap, 0.0);

            // Squeeze eye height when opening/closing (vertical scale)
            float eyeH = eyeSize * 0.7 * openAmount;
            float eyeW = eyeSize * 1.6;

            // Left eye
            vec2 lScaled = leftEye / vec2(eyeW, max(eyeH, 0.001));
            float lDist = length(lScaled);
            float lMask = 1.0 - smoothstep(0.85, 1.0, lDist);
            float lSlitW = eyeSize * 0.12;
            float lSlitH = eyeSize * 0.55 * openAmount;
            vec2 lSlitScaled = leftEye / vec2(lSlitW, max(lSlitH, 0.001));
            float lSlit = 1.0 - smoothstep(0.7, 1.0, length(lSlitScaled));
            float le = lMask - lSlit * 0.7;

            // Right eye
            vec2 rScaled = rightEye / vec2(eyeW, max(eyeH, 0.001));
            float rDist = length(rScaled);
            float rMask = 1.0 - smoothstep(0.85, 1.0, rDist);
            vec2 rSlitScaled = rightEye / vec2(lSlitW, max(lSlitH, 0.001));
            float rSlit = 1.0 - smoothstep(0.7, 1.0, length(rSlitScaled));
            float re = rMask - rSlit * 0.7;

            eyeGlow = max(le, re) * openAmount;
        }
    }
    eyeGlow = clamp(eyeGlow, 0.0, 1.0);

    // ─── Output ────────────────────────────────────────────
    // Base: very dark blue-black
    vec3 voidColor = vec3(0.02, 0.015, 0.04);
    // Swirl tendrils: slightly lighter purple-dark
    vec3 swirlColor = vec3(0.06, 0.03, 0.08);
    // Dark pockets: even darker
    vec3 pocketColor = vec3(0.01, 0.005, 0.02);
    vec3 baseColor = mix(voidColor, swirlColor, n * 0.6);
    baseColor = mix(baseColor, pocketColor, pocketMask * 0.7);

    // Eye glow: sickly yellow-green cat eyes
    vec3 eyeColor = vec3(0.6, 0.8, 0.1);
    baseColor = mix(baseColor, eyeColor, eyeGlow * 0.8);

    float alpha = darkness * mask * uIntensity;

    // Eyes punch through the darkness slightly
    alpha = max(alpha, eyeGlow * 0.5 * mask);

    gl_FragColor = vec4(baseColor * alpha, alpha);
}
`;

export class EffectsLayer {
  public container: PIXI.Container;
  private effects: Map<string, ActiveAreaEffect> = new Map();
  private ticker: PIXI.Ticker | null = null;
  private gridSize: number;
  private unsubscribe: (() => void) | null = null;
  private selectedEffectId: string | null = null;
  private dragState: { effectId: string; startX: number; startY: number } | null = null;

  // Sub-containers: non-darkness effects get masked, darkness renders on top
  private nonDarkContainer: PIXI.Container;
  private darkContainer: PIXI.Container;
  private darknessMask: PIXI.Graphics;

  constructor(gridSize: number) {
    this.container = new PIXI.Container();
    this.container.zIndex = 4;
    this.container.sortableChildren = true;
    this.gridSize = gridSize;

    // Non-darkness effects (fire, ice, etc) — these get masked by darkness areas
    this.nonDarkContainer = new PIXI.Container();
    this.nonDarkContainer.zIndex = 0;
    this.container.addChild(this.nonDarkContainer);

    // Mask graphics: full rect with darkness areas punched out as holes
    this.darknessMask = new PIXI.Graphics();
    this.container.addChild(this.darknessMask);
    this.nonDarkContainer.mask = this.darknessMask;

    // Darkness effects render on top
    this.darkContainer = new PIXI.Container();
    this.darkContainer.zIndex = 100;
    this.container.addChild(this.darkContainer);

    // Initialize mask to show everything (no darkness yet)
    this.darknessMask.beginFill(0xffffff);
    this.darknessMask.drawRect(-10000, -10000, 20000, 20000);
    this.darknessMask.endFill();

    this.unsubscribe = useEffectStore.subscribe((state, prev) => {
      if (state.activeEffects !== prev.activeEffects) {
        this.syncEffects(state.activeEffects);
      }
      if (state.selectedEffectId !== prev.selectedEffectId) {
        this.selectedEffectId = state.selectedEffectId;
      }
    });

    this.syncEffects(useEffectStore.getState().activeEffects);
  }

  setTicker(ticker: PIXI.Ticker): void {
    this.ticker = ticker;
    this.ticker.add(this.update, this);
  }

  setGridSize(size: number): void {
    this.gridSize = size;
    // Rebuild shader meshes (geometry depends on gridSize) and re-render
    for (const active of this.effects.values()) {
      if (active.fireMesh && active.effect.effect === 'fire') {
        active.container.removeChild(active.fireMesh);
        active.fireMesh.destroy();
        try {
          const { mesh, shader } = this.createFireMesh(active.effect);
          active.container.addChildAt(mesh, 0);
          active.fireMesh = mesh;
          active.fireShader = shader;
        } catch (e) {
          console.warn('Fire shader rebuild failed:', e);
          active.fireMesh = undefined;
          active.fireShader = undefined;
        }
      }
      if (active.iceMesh && active.effect.effect === 'ice') {
        active.container.removeChild(active.iceMesh);
        active.iceMesh.destroy();
        try {
          const { mesh, shader } = this.createIceMesh(active.effect);
          active.container.addChildAt(mesh, 0);
          active.iceMesh = mesh;
          active.iceShader = shader;
        } catch (e) {
          console.warn('Blizzard shader rebuild failed:', e);
          active.iceMesh = undefined;
          active.iceShader = undefined;
        }
      }
      if (active.decayMesh && active.effect.effect === 'decay') {
        active.container.removeChild(active.decayMesh);
        active.decayMesh.destroy();
        try {
          const { mesh, shader } = this.createDecayMesh(active.effect);
          active.container.addChildAt(mesh, 0);
          active.decayMesh = mesh;
          active.decayShader = shader;
        } catch (e) {
          console.warn('Decay shader rebuild failed:', e);
          active.decayMesh = undefined;
          active.decayShader = undefined;
        }
      }
      if (active.feyMesh && active.effect.effect === 'fey') {
        active.container.removeChild(active.feyMesh);
        active.feyMesh.destroy();
        try {
          const { mesh, shader } = this.createFeyMesh(active.effect);
          active.container.addChildAt(mesh, 0);
          active.feyMesh = mesh;
          active.feyShader = shader;
        } catch (e) {
          console.warn('Fey shader rebuild failed:', e);
          active.feyMesh = undefined;
          active.feyShader = undefined;
        }
      }
      if (active.darkMesh && active.effect.effect === 'darkness') {
        active.container.removeChild(active.darkMesh);
        active.darkMesh.destroy();
        try {
          const { mesh, shader } = this.createDarkMesh(active.effect);
          active.container.addChildAt(mesh, 0);
          active.darkMesh = mesh;
          active.darkShader = shader;
        } catch (e) {
          console.warn('Darkness shader rebuild failed:', e);
          active.darkMesh = undefined;
          active.darkShader = undefined;
        }
      }
      this.renderEffect(active);
    }
  }

  // ─── Fire Mesh Creation ─────────────────────────────────

  private createFireMesh(effect: SpellAreaEffect): { mesh: PIXI.Mesh; shader: PIXI.Shader } {
    const radiusPx = effect.radiusSquares * this.gridSize;

    // Shape-dependent geometry bounds and UV mapping
    let extLeft: number, extRight: number, extUp: number, extDown: number;
    let originUV: [number, number];
    let shapeIndex: number;
    let lineHalfRadius = 0;

    switch (effect.shape) {
      case 'cone': {
        // Cone extends in +x direction from origin, 60-degree wedge
        // Need extra padding in all directions for flame wisps
        const coneHalf = Math.PI / 6; // 30 degrees
        const ySpread = Math.sin(coneHalf) * radiusPx;
        extLeft = radiusPx * 0.3;   // small padding behind origin
        extRight = radiusPx * 1.4;  // extend past cone tip
        extUp = ySpread * 1.5 + radiusPx * 0.3;  // above for rising flames
        extDown = ySpread * 1.2;
        shapeIndex = 2.0;
        break;
      }
      case 'line': {
        // Line extends in +x direction, width = 1 grid square
        const halfWidth = this.gridSize * 0.5;
        lineHalfRadius = halfWidth / radiusPx; // in radius-normalized units
        extLeft = radiusPx * 0.15;
        extRight = radiusPx * 1.3;
        extUp = halfWidth * 2.0 + radiusPx * 0.3; // extra above for rising flames
        extDown = halfWidth * 1.5;
        shapeIndex = 3.0;
        break;
      }
      case 'square': {
        extLeft = radiusPx * 1.3;
        extRight = radiusPx * 1.3;
        extUp = radiusPx * 1.6;
        extDown = radiusPx * 1.1;
        shapeIndex = 1.0;
        break;
      }
      default: { // circle
        extLeft = radiusPx * 1.3;
        extRight = radiusPx * 1.3;
        extUp = radiusPx * 1.6;
        extDown = radiusPx * 1.1;
        shapeIndex = 0.0;
        break;
      }
    }

    const totalW = extLeft + extRight;
    const totalH = extUp + extDown;

    // Origin in UV space — where (0,0) in local coordinates maps to in UV
    originUV = [extLeft / totalW, extUp / totalH];

    // Geometry: quad positioned so origin is at the correct UV position
    const geometry = new PIXI.Geometry()
      .addAttribute('aVertexPosition', new Float32Array([
        -extLeft, -extUp,     // top-left
         extRight, -extUp,    // top-right
         extRight,  extDown,  // bottom-right
        -extLeft,  extDown,   // bottom-left
      ]), 2)
      .addAttribute('aUvs', new Float32Array([
        0, 0,   1, 0,   1, 1,   0, 1,
      ]), 2)
      .addIndex([0, 1, 2, 0, 2, 3]);

    const env = useMapStore.getState().activeMap?.environment;
    const skin = getFlameSkin(env);

    const shader = PIXI.Shader.from(FIRE_VERT, FIRE_FRAG, {
      uTime: 0.0,
      uIntensity: effect.alpha,
      uOriginUV: originUV,
      // Scale: maps UV to radius-normalized coordinates
      uScale: [totalW / radiusPx, totalH / radiusPx],
      uShape: shapeIndex,
      uLineHalf: lineHalfRadius,
      uRamp0: skin.ramp[0], uRamp1: skin.ramp[1], uRamp2: skin.ramp[2],
      uRamp3: skin.ramp[3], uRamp4: skin.ramp[4], uRamp5: skin.ramp[5],
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mesh = new PIXI.Mesh(geometry, shader as any);
    mesh.position.set(effect.origin.x, effect.origin.y);
    mesh.blendMode = PIXI.BLEND_MODES.ADD;

    return { mesh, shader };
  }

  // ─── Ice/Blizzard Mesh Creation ────────────────────────────

  private createIceMesh(effect: SpellAreaEffect): { mesh: PIXI.Mesh; shader: PIXI.Shader } {
    const radiusPx = effect.radiusSquares * this.gridSize;

    // Same geometry logic as fire — symmetric padding for blizzard fog
    let extLeft: number, extRight: number, extUp: number, extDown: number;
    let originUV: [number, number];
    let shapeIndex: number;
    let lineHalfRadius = 0;

    switch (effect.shape) {
      case 'cone': {
        const coneHalf = Math.PI / 6;
        const ySpread = Math.sin(coneHalf) * radiusPx;
        extLeft = radiusPx * 0.3;
        extRight = radiusPx * 1.3;
        extUp = ySpread * 1.3;
        extDown = ySpread * 1.3;
        shapeIndex = 2.0;
        break;
      }
      case 'line': {
        const halfWidth = this.gridSize * 0.5;
        lineHalfRadius = halfWidth / radiusPx;
        extLeft = radiusPx * 0.15;
        extRight = radiusPx * 1.3;
        extUp = halfWidth * 1.5;
        extDown = halfWidth * 1.5;
        shapeIndex = 3.0;
        break;
      }
      case 'square': {
        extLeft = radiusPx * 1.2;
        extRight = radiusPx * 1.2;
        extUp = radiusPx * 1.2;
        extDown = radiusPx * 1.2;
        shapeIndex = 1.0;
        break;
      }
      default: { // circle
        extLeft = radiusPx * 1.2;
        extRight = radiusPx * 1.2;
        extUp = radiusPx * 1.2;
        extDown = radiusPx * 1.2;
        shapeIndex = 0.0;
        break;
      }
    }

    const totalW = extLeft + extRight;
    const totalH = extUp + extDown;
    originUV = [extLeft / totalW, extUp / totalH];

    const geometry = new PIXI.Geometry()
      .addAttribute('aVertexPosition', new Float32Array([
        -extLeft, -extUp,
         extRight, -extUp,
         extRight,  extDown,
        -extLeft,  extDown,
      ]), 2)
      .addAttribute('aUvs', new Float32Array([
        0, 0,   1, 0,   1, 1,   0, 1,
      ]), 2)
      .addIndex([0, 1, 2, 0, 2, 3]);

    const shader = PIXI.Shader.from(ICE_VERT, ICE_FRAG, {
      uTime: 0.0,
      uIntensity: effect.alpha * 0.7, // slightly less intense than fire
      uOriginUV: originUV,
      uScale: [totalW / radiusPx, totalH / radiusPx],
      uShape: shapeIndex,
      uLineHalf: lineHalfRadius,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mesh = new PIXI.Mesh(geometry, shader as any);
    mesh.position.set(effect.origin.x, effect.origin.y);
    mesh.blendMode = PIXI.BLEND_MODES.NORMAL; // Fog obscures, not additive

    return { mesh, shader };
  }

  // ─── Decay/Ooze Mesh Creation ──────────────────────────────

  private createDecayMesh(effect: SpellAreaEffect): { mesh: PIXI.Mesh; shader: PIXI.Shader } {
    const radiusPx = effect.radiusSquares * this.gridSize;

    let extLeft: number, extRight: number, extUp: number, extDown: number;
    let originUV: [number, number];
    let shapeIndex: number;
    let lineHalfRadius = 0;

    switch (effect.shape) {
      case 'cone': {
        const coneHalf = Math.PI / 6;
        const ySpread = Math.sin(coneHalf) * radiusPx;
        extLeft = radiusPx * 0.3;
        extRight = radiusPx * 1.3;
        extUp = ySpread * 1.3;
        extDown = ySpread * 1.5; // extra below for dripping
        shapeIndex = 2.0;
        break;
      }
      case 'line': {
        const halfWidth = this.gridSize * 0.5;
        lineHalfRadius = halfWidth / radiusPx;
        extLeft = radiusPx * 0.15;
        extRight = radiusPx * 1.3;
        extUp = halfWidth * 1.5;
        extDown = halfWidth * 1.8;
        shapeIndex = 3.0;
        break;
      }
      case 'square': {
        extLeft = radiusPx * 1.2;
        extRight = radiusPx * 1.2;
        extUp = radiusPx * 1.2;
        extDown = radiusPx * 1.4; // extra below for pooling
        shapeIndex = 1.0;
        break;
      }
      default: {
        extLeft = radiusPx * 1.2;
        extRight = radiusPx * 1.2;
        extUp = radiusPx * 1.2;
        extDown = radiusPx * 1.4;
        shapeIndex = 0.0;
        break;
      }
    }

    const totalW = extLeft + extRight;
    const totalH = extUp + extDown;
    originUV = [extLeft / totalW, extUp / totalH];

    const geometry = new PIXI.Geometry()
      .addAttribute('aVertexPosition', new Float32Array([
        -extLeft, -extUp,
         extRight, -extUp,
         extRight,  extDown,
        -extLeft,  extDown,
      ]), 2)
      .addAttribute('aUvs', new Float32Array([
        0, 0,   1, 0,   1, 1,   0, 1,
      ]), 2)
      .addIndex([0, 1, 2, 0, 2, 3]);

    const shader = PIXI.Shader.from(DECAY_VERT, DECAY_FRAG, {
      uTime: 0.0,
      uIntensity: effect.alpha * 0.85,
      uOriginUV: originUV,
      uScale: [totalW / radiusPx, totalH / radiusPx],
      uShape: shapeIndex,
      uLineHalf: lineHalfRadius,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mesh = new PIXI.Mesh(geometry, shader as any);
    mesh.position.set(effect.origin.x, effect.origin.y);
    mesh.blendMode = PIXI.BLEND_MODES.ADD; // Toxic glow

    return { mesh, shader };
  }

  // ─── Fey Mesh Creation ──────────────────────────────────

  private createFeyMesh(effect: SpellAreaEffect): { mesh: PIXI.Mesh; shader: PIXI.Shader } {
    const radiusPx = effect.radiusSquares * this.gridSize;

    let extLeft: number, extRight: number, extUp: number, extDown: number;
    let originUV: [number, number];
    let shapeIndex: number;
    let lineHalfRadius = 0;

    switch (effect.shape) {
      case 'cone': {
        const coneHalf = Math.PI / 6;
        const ySpread = Math.sin(coneHalf) * radiusPx;
        extLeft = radiusPx * 0.3;
        extRight = radiusPx * 1.3;
        extUp = ySpread * 1.3;
        extDown = ySpread * 1.3;
        shapeIndex = 2.0;
        break;
      }
      case 'line': {
        const halfWidth = this.gridSize * 0.5;
        lineHalfRadius = halfWidth / radiusPx;
        extLeft = radiusPx * 0.15;
        extRight = radiusPx * 1.3;
        extUp = halfWidth * 1.5;
        extDown = halfWidth * 1.5;
        shapeIndex = 3.0;
        break;
      }
      case 'square': {
        extLeft = radiusPx * 1.2;
        extRight = radiusPx * 1.2;
        extUp = radiusPx * 1.2;
        extDown = radiusPx * 1.2;
        shapeIndex = 1.0;
        break;
      }
      default: {
        extLeft = radiusPx * 1.2;
        extRight = radiusPx * 1.2;
        extUp = radiusPx * 1.2;
        extDown = radiusPx * 1.2;
        shapeIndex = 0.0;
        break;
      }
    }

    const totalW = extLeft + extRight;
    const totalH = extUp + extDown;
    originUV = [extLeft / totalW, extUp / totalH];

    const geometry = new PIXI.Geometry()
      .addAttribute('aVertexPosition', new Float32Array([
        -extLeft, -extUp,
         extRight, -extUp,
         extRight,  extDown,
        -extLeft,  extDown,
      ]), 2)
      .addAttribute('aUvs', new Float32Array([
        0, 0,   1, 0,   1, 1,   0, 1,
      ]), 2)
      .addIndex([0, 1, 2, 0, 2, 3]);

    const shader = PIXI.Shader.from(FEY_VERT, FEY_FRAG, {
      uTime: 0.0,
      uIntensity: effect.alpha * 0.9,
      uOriginUV: originUV,
      uScale: [totalW / radiusPx, totalH / radiusPx],
      uShape: shapeIndex,
      uLineHalf: lineHalfRadius,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mesh = new PIXI.Mesh(geometry, shader as any);
    mesh.position.set(effect.origin.x, effect.origin.y);
    mesh.blendMode = PIXI.BLEND_MODES.ADD; // Ethereal glow

    return { mesh, shader };
  }

  // ─── Darkness Mesh Creation ─────────────────────────────

  private createDarkMesh(effect: SpellAreaEffect): { mesh: PIXI.Mesh; shader: PIXI.Shader } {
    const radiusPx = effect.radiusSquares * this.gridSize;

    let extLeft: number, extRight: number, extUp: number, extDown: number;
    let originUV: [number, number];
    let shapeIndex: number;
    let lineHalfRadius = 0;

    switch (effect.shape) {
      case 'cone': {
        const coneHalf = Math.PI / 6;
        const ySpread = Math.sin(coneHalf) * radiusPx;
        extLeft = radiusPx * 0.3;
        extRight = radiusPx * 1.3;
        extUp = ySpread * 1.3;
        extDown = ySpread * 1.3;
        shapeIndex = 2.0;
        break;
      }
      case 'line': {
        const halfWidth = this.gridSize * 0.5;
        lineHalfRadius = halfWidth / radiusPx;
        extLeft = radiusPx * 0.15;
        extRight = radiusPx * 1.3;
        extUp = halfWidth * 1.5;
        extDown = halfWidth * 1.5;
        shapeIndex = 3.0;
        break;
      }
      case 'square': {
        extLeft = radiusPx * 1.2;
        extRight = radiusPx * 1.2;
        extUp = radiusPx * 1.2;
        extDown = radiusPx * 1.2;
        shapeIndex = 1.0;
        break;
      }
      default: {
        extLeft = radiusPx * 1.2;
        extRight = radiusPx * 1.2;
        extUp = radiusPx * 1.2;
        extDown = radiusPx * 1.2;
        shapeIndex = 0.0;
        break;
      }
    }

    const totalW = extLeft + extRight;
    const totalH = extUp + extDown;
    originUV = [extLeft / totalW, extUp / totalH];

    const geometry = new PIXI.Geometry()
      .addAttribute('aVertexPosition', new Float32Array([
        -extLeft, -extUp,
         extRight, -extUp,
         extRight,  extDown,
        -extLeft,  extDown,
      ]), 2)
      .addAttribute('aUvs', new Float32Array([
        0, 0,   1, 0,   1, 1,   0, 1,
      ]), 2)
      .addIndex([0, 1, 2, 0, 2, 3]);

    const shader = PIXI.Shader.from(DARK_VERT, DARK_FRAG, {
      uTime: 0.0,
      uIntensity: effect.alpha,
      uOriginUV: originUV,
      uScale: [totalW / radiusPx, totalH / radiusPx],
      uShape: shapeIndex,
      uLineHalf: lineHalfRadius,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mesh = new PIXI.Mesh(geometry, shader as any);
    mesh.position.set(effect.origin.x, effect.origin.y);
    mesh.blendMode = PIXI.BLEND_MODES.NORMAL; // Opaque darkness, not additive

    return { mesh, shader };
  }

  // ─── Sync ────────────────────────────────────────────────────

  private syncEffects(effects: Map<string, SpellAreaEffect>): void {
    for (const [id, effect] of effects) {
      const existing = this.effects.get(id);
      if (!existing) {
        this.addEffect(effect);
      } else if (
        existing.effect.origin.x !== effect.origin.x ||
        existing.effect.origin.y !== effect.origin.y
      ) {
        const dx = effect.origin.x - existing.effect.origin.x;
        const dy = effect.origin.y - existing.effect.origin.y;
        existing.effect = effect;
        for (const p of existing.particles) { p.x += dx; p.y += dy; }
        this.drawHitArea(existing.hitArea, effect);
        // Update fire mesh position
        if (existing.fireMesh) {
          existing.fireMesh.position.set(effect.origin.x, effect.origin.y);
        }
        if (existing.iceMesh) {
          existing.iceMesh.position.set(effect.origin.x, effect.origin.y);
        }
        if (existing.decayMesh) {
          existing.decayMesh.position.set(effect.origin.x, effect.origin.y);
        }
        if (existing.feyMesh) {
          existing.feyMesh.position.set(effect.origin.x, effect.origin.y);
        }
        if (existing.darkMesh) {
          existing.darkMesh.position.set(effect.origin.x, effect.origin.y);
          this.updateDarknessMask();
        }
      }
    }

    for (const id of this.effects.keys()) {
      if (!effects.has(id)) {
        this.removeEffect(id);
      }
    }
  }

  private addEffect(effect: SpellAreaEffect): void {
    const effectContainer = new PIXI.Container();
    const graphics = new PIXI.Graphics();
    const hitArea = new PIXI.Graphics();
    hitArea.alpha = 0;

    // For shader-based effects: create mesh first so it renders below particles/selection
    let fireMesh: PIXI.Mesh | undefined;
    let fireShader: PIXI.Shader | undefined;
    let iceMesh: PIXI.Mesh | undefined;
    let iceShader: PIXI.Shader | undefined;
    let decayMesh: PIXI.Mesh | undefined;
    let decayShader: PIXI.Shader | undefined;
    let feyMesh: PIXI.Mesh | undefined;
    let feyShader: PIXI.Shader | undefined;
    let darkMesh: PIXI.Mesh | undefined;
    let darkShader: PIXI.Shader | undefined;
    if (effect.effect === 'fire') {
      try {
        const result = this.createFireMesh(effect);
        fireMesh = result.mesh;
        fireShader = result.shader;
        effectContainer.addChild(fireMesh);
      } catch (e) {
        console.warn('Fire shader creation failed, using particle fallback:', e);
      }
    }
    if (effect.effect === 'ice') {
      try {
        const result = this.createIceMesh(effect);
        iceMesh = result.mesh;
        iceShader = result.shader;
        effectContainer.addChild(iceMesh);
      } catch (e) {
        console.warn('Blizzard shader creation failed, using particle fallback:', e);
      }
    }
    if (effect.effect === 'decay') {
      try {
        const result = this.createDecayMesh(effect);
        decayMesh = result.mesh;
        decayShader = result.shader;
        effectContainer.addChild(decayMesh);
      } catch (e) {
        console.warn('Decay shader creation failed, using particle fallback:', e);
      }
    }
    if (effect.effect === 'fey') {
      try {
        const result = this.createFeyMesh(effect);
        feyMesh = result.mesh;
        feyShader = result.shader;
        effectContainer.addChild(feyMesh);
      } catch (e) {
        console.warn('Fey shader creation failed, using particle fallback:', e);
      }
    }
    if (effect.effect === 'darkness') {
      try {
        const result = this.createDarkMesh(effect);
        darkMesh = result.mesh;
        darkShader = result.shader;
        effectContainer.addChild(darkMesh);
      } catch (e) {
        console.warn('Darkness shader creation failed, using overlay fallback:', e);
      }
    }

    effectContainer.addChild(graphics);
    effectContainer.addChild(hitArea);

    // Darkness goes in its own container; everything else gets masked
    if (effect.effect === 'darkness') {
      this.darkContainer.addChild(effectContainer);
    } else {
      this.nonDarkContainer.addChild(effectContainer);
    }

    hitArea.eventMode = 'static';
    hitArea.cursor = 'pointer';
    hitArea.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
      e.stopPropagation();
      setPixiEffectClicked(true);
      useEffectStore.getState().selectEffect(effect.id);
      this.dragState = { effectId: effect.id, startX: e.global.x, startY: e.global.y };
      hitArea.cursor = 'grabbing';
    });
    hitArea.on('globalpointermove', (e: PIXI.FederatedPointerEvent) => {
      if (!this.dragState || this.dragState.effectId !== effect.id) return;
      const active = this.effects.get(effect.id);
      if (!active) return;
      active.container.x = e.global.x - this.dragState.startX;
      active.container.y = e.global.y - this.dragState.startY;
    });
    const onDragEnd = (e: PIXI.FederatedPointerEvent) => {
      if (!this.dragState || this.dragState.effectId !== effect.id) return;
      const active = this.effects.get(effect.id);
      if (!active) { this.dragState = null; return; }

      const dx = e.global.x - this.dragState.startX;
      const dy = e.global.y - this.dragState.startY;
      this.dragState = null;
      hitArea.cursor = 'pointer';

      if (Math.abs(dx) < 3 && Math.abs(dy) < 3) {
        active.container.x = 0;
        active.container.y = 0;
        return;
      }

      const rawX = active.effect.origin.x + dx;
      const rawY = active.effect.origin.y + dy;
      const snappedX = Math.round(rawX / this.gridSize) * this.gridSize;
      const snappedY = Math.round(rawY / this.gridSize) * this.gridSize;

      active.container.x = 0;
      active.container.y = 0;
      active.effect = { ...active.effect, origin: { x: snappedX, y: snappedY } };

      const ox = snappedX - rawX;
      for (const p of active.particles) {
        p.x += dx + ox;
        p.y += dy + (snappedY - rawY);
      }

      // Update fire mesh position after drag
      if (active.fireMesh) {
        active.fireMesh.position.set(snappedX, snappedY);
      }
      if (active.iceMesh) {
        active.iceMesh.position.set(snappedX, snappedY);
      }
      if (active.decayMesh) {
        active.decayMesh.position.set(snappedX, snappedY);
      }
      if (active.feyMesh) {
        active.feyMesh.position.set(snappedX, snappedY);
      }
      if (active.darkMesh) {
        active.darkMesh.position.set(snappedX, snappedY);
        this.updateDarknessMask();
      }

      this.drawHitArea(active.hitArea, active.effect);
      useEffectStore.getState().moveEffect(effect.id, { x: snappedX, y: snappedY });
    };
    hitArea.on('pointerup', onDragEnd);
    hitArea.on('pointerupoutside', onDragEnd);

    this.drawHitArea(hitArea, effect);

    const particles = this.createParticles(effect);
    const active: ActiveAreaEffect = {
      effect,
      container: effectContainer,
      graphics,
      hitArea,
      particles,
      elapsed: 0,
      fireMesh,
      fireShader,
      iceMesh,
      iceShader,
      decayMesh,
      decayShader,
      feyMesh,
      feyShader,
      darkMesh,
      darkShader,
    };

    this.effects.set(effect.id, active);
    this.renderEffect(active);

    // Rebuild mask if a darkness area was added
    if (effect.effect === 'darkness') {
      this.updateDarknessMask();
    }
  }

  /**
   * Rebuild the mask that clips non-darkness effects.
   * Draws a huge rect (visible everywhere), then punches holes where
   * each darkness area is — so fire/ice/etc disappear inside darkness.
   */
  private updateDarknessMask(): void {
    const g = this.darknessMask;
    g.clear();

    // Collect darkness effect areas
    const darkAreas: Array<{ x: number; y: number; radius: number; shape: string }> = [];
    for (const active of this.effects.values()) {
      if (active.effect.effect === 'darkness') {
        darkAreas.push({
          x: active.effect.origin.x,
          y: active.effect.origin.y,
          radius: active.effect.radiusSquares * this.gridSize,
          shape: active.effect.shape,
        });
      }
    }

    if (darkAreas.length === 0) {
      // No darkness — draw full rect so everything is visible
      g.beginFill(0xffffff);
      g.drawRect(-10000, -10000, 20000, 20000);
      g.endFill();
      return;
    }

    // Draw full rect (everything visible), then cut holes where darkness is
    g.beginFill(0xffffff);
    g.drawRect(-10000, -10000, 20000, 20000);

    for (const area of darkAreas) {
      g.beginHole();
      switch (area.shape) {
        case 'square':
          g.drawRect(
            area.x - area.radius,
            area.y - area.radius,
            area.radius * 2,
            area.radius * 2,
          );
          break;
        default: // circle (and fallback for cone/line)
          g.drawCircle(area.x, area.y, area.radius);
          break;
      }
      g.endHole();
    }

    g.endFill();
  }

  private drawHitArea(g: PIXI.Graphics, effect: SpellAreaEffect): void {
    const radiusPx = effect.radiusSquares * this.gridSize;
    const { x, y } = effect.origin;
    g.clear();
    g.beginFill(0xffffff, 0.01);
    switch (effect.shape) {
      case 'circle':
        g.drawCircle(x, y, radiusPx);
        break;
      case 'square': {
        const half = radiusPx;
        g.drawRect(x - half, y - half, half * 2, half * 2);
        break;
      }
      case 'cone': {
        const coneAngle = Math.PI / 3;
        g.moveTo(x, y);
        g.arc(x, y, radiusPx, -coneAngle / 2, coneAngle / 2);
        g.closePath();
        break;
      }
      case 'line': {
        const lineWidth = this.gridSize;
        g.drawRect(x, y - lineWidth / 2, radiusPx, lineWidth);
        break;
      }
    }
    g.endFill();
  }

  private removeEffect(id: string): void {
    const active = this.effects.get(id);
    if (active) {
      // Clean up shader mesh resources
      if (active.fireMesh) {
        active.fireMesh.destroy();
      }
      if (active.iceMesh) {
        active.iceMesh.destroy();
      }
      if (active.decayMesh) {
        active.decayMesh.destroy();
      }
      if (active.feyMesh) {
        active.feyMesh.destroy();
      }
      if (active.darkMesh) {
        active.darkMesh.destroy();
      }
      const isDark = active.effect.effect === 'darkness';
      const parent = isDark ? this.darkContainer : this.nonDarkContainer;
      parent.removeChild(active.container);
      active.container.destroy({ children: true });
      this.effects.delete(id);

      // Rebuild mask if a darkness area was removed
      if (isDark) {
        this.updateDarknessMask();
      }
    }
  }

  // ─── Particles ───────────────────────────────────────────────

  private createParticles(effect: SpellAreaEffect): EffectParticle[] {
    const colors = EFFECT_COLORS[effect.effect];
    const radiusPx = effect.radiusSquares * this.gridSize;
    const count = PARTICLE_COUNT[effect.effect] || 25;
    const particles: EffectParticle[] = [];

    for (let i = 0; i < count; i++) {
      // Assign layers: ~30% inner, ~40% mid, ~30% outer
      const layer = i < count * 0.3 ? 0 : i < count * 0.7 ? 1 : 2;
      const pos = this.spawnPosition(effect.effect, effect.origin, radiusPx, layer, effect.shape);
      const vel = this.spawnVelocity(effect.effect, layer);
      const seed = Math.random() * 100;
      particles.push({
        ...pos,
        ...vel,
        life: Math.random() * 2,
        maxLife: this.particleMaxLife(effect.effect, layer),
        size: this.particleSize(effect.effect, layer),
        color: this.pickColor(colors, effect.effect, i, layer),
        alpha: this.particleAlpha(effect.effect, layer),
        rotation: effect.effect === 'thunder'
          ? Math.atan2(pos.y - effect.origin.y, pos.x - effect.origin.x)
          : Math.random() * Math.PI * 2,
        rotSpeed: this.particleRotSpeed(effect.effect),
        variant: Math.floor(Math.random() * 6),
        seed,
        layer,
      });
    }
    return particles;
  }

  private spawnPosition(type: EffectName, origin: { x: number; y: number }, radiusPx: number, layer: number, shape: string = 'circle'): { x: number; y: number } {
    const angle = Math.random() * Math.PI * 2;

    if (type === 'fire') {
      // Embers: spawn within the effect shape, biased upward
      if (shape === 'cone') {
        // Cone: 60-degree wedge in +x direction
        const coneHalf = Math.PI / 6;
        const a = (Math.random() - 0.5) * coneHalf * 2;
        const dist = Math.random() * radiusPx * 0.9;
        return {
          x: origin.x + Math.cos(a) * dist,
          y: origin.y + Math.sin(a) * dist - Math.random() * radiusPx * 0.3,
        };
      }
      if (shape === 'line') {
        // Line: extends in +x direction, width = 1 grid square
        const halfW = this.gridSize * 0.5;
        return {
          x: origin.x + Math.random() * radiusPx,
          y: origin.y + (Math.random() - 0.5) * halfW - Math.random() * halfW * 0.5,
        };
      }
      // Circle/square: generic spread
      const spread = 0.5 + layer * 0.2;
      const dx = (Math.random() - 0.5) * radiusPx * spread * 2;
      // Biased upward: embers spawn from center to above the fire
      const dy = (Math.random() - 0.7) * radiusPx * 1.2;
      return { x: origin.x + dx, y: origin.y + dy };
    }

    if (type === 'thunder') {
      if (shape === 'cone') {
        const coneHalf = Math.PI / 6;
        const a = (Math.random() - 0.5) * coneHalf * 2;
        const dist = Math.random() * radiusPx * 0.85;
        return { x: origin.x + Math.cos(a) * dist, y: origin.y + Math.sin(a) * dist };
      }
      if (shape === 'line') {
        const halfW = this.gridSize * 0.5;
        return { x: origin.x + Math.random() * radiusPx, y: origin.y + (Math.random() - 0.5) * halfW };
      }
      // Circle/square: spawn across the entire area
      const dist = Math.random() * radiusPx * 0.85;
      return {
        x: origin.x + Math.cos(angle) * dist,
        y: origin.y + Math.sin(angle) * dist,
      };
    }

    if (type === 'ice') {
      if (shape === 'cone') {
        const coneHalf = Math.PI / 6;
        const a = (Math.random() - 0.5) * coneHalf * 2;
        const d = Math.random() * radiusPx * 1.1;
        return {
          x: origin.x + Math.cos(a) * d - radiusPx * 0.15,
          y: origin.y + Math.sin(a) * d,
        };
      }
      if (shape === 'line') {
        const halfW = this.gridSize * 0.5;
        return {
          x: origin.x + Math.random() * radiusPx - radiusPx * 0.15,
          y: origin.y + (Math.random() - 0.5) * halfW,
        };
      }
      // Circle/square: blizzard with slight upwind bias
      const a = Math.random() * Math.PI * 2;
      const d = Math.random() * radiusPx * 1.1; // allow slight overflow
      return {
        x: origin.x + Math.cos(a) * d - radiusPx * 0.2, // subtle left bias
        y: origin.y + Math.sin(a) * d,
      };
    }

    // Default: shape-aware spawn
    if (shape === 'cone') {
      // Cone: 60-degree wedge in +x direction
      const coneHalf = Math.PI / 6;
      const a = (Math.random() - 0.5) * coneHalf * 2;
      const dist = Math.random() * radiusPx * 0.9;
      return {
        x: origin.x + Math.cos(a) * dist,
        y: origin.y + Math.sin(a) * dist,
      };
    }
    if (shape === 'line') {
      const halfW = this.gridSize * 0.5;
      return {
        x: origin.x + Math.random() * radiusPx,
        y: origin.y + (Math.random() - 0.5) * halfW,
      };
    }

    // Circle/square: random within radius
    const dist = Math.random() * radiusPx * 0.9;
    let x = origin.x + Math.cos(angle) * dist;
    let y = origin.y + Math.sin(angle) * dist;

    if (type === 'decay') {
      y = origin.y - Math.abs(Math.sin(angle)) * dist * 0.3;
    }

    return { x, y };
  }

  private spawnVelocity(type: EffectName, _layer: number): { vx: number; vy: number } {
    switch (type) {
      case 'fire': {
        // Embers: fast upward drift with random sideways scatter
        const upSpeed = -(30 + Math.random() * 60);
        const sideSpeed = (Math.random() - 0.5) * 40;
        return { vx: sideSpeed, vy: upSpeed };
      }
      case 'thunder':
        return { vx: 0, vy: 0 }; // stationary — bolts animate in place
      case 'ice': {
        // Blizzard: driving horizontal wind with downward angle
        const windSpeed = 60 + Math.random() * 80;
        const downDrift = 15 + Math.random() * 30;
        return { vx: windSpeed + (Math.random() - 0.5) * 20, vy: downDrift + (Math.random() - 0.5) * 15 };
      }
      case 'divine':
        return { vx: (Math.random() - 0.5) * 8, vy: -(10 + Math.random() * 15) };
      case 'decay':
        return { vx: (Math.random() - 0.5) * 6, vy: 5 + Math.random() * 14 };
      case 'eldritch':
        return { vx: (Math.random() - 0.5) * 20, vy: (Math.random() - 0.5) * 20 };
      case 'psychic': {
        const a = Math.random() * Math.PI * 2;
        const spd = 12 + Math.random() * 18;
        return { vx: Math.cos(a) * spd, vy: Math.sin(a) * spd };
      }
      case 'menace':
        return { vx: (Math.random() - 0.5) * 12, vy: -(4 + Math.random() * 10) };
      case 'fey':
        return { vx: (Math.random() - 0.5) * 16, vy: -(6 + Math.random() * 12) };
      case 'darkness':
        return { vx: (Math.random() - 0.5) * 3, vy: (Math.random() - 0.5) * 2 }; // very slow drift
      default:
        return { vx: (Math.random() - 0.5) * 10, vy: (Math.random() - 0.5) * 10 };
    }
  }

  private particleMaxLife(type: EffectName, _layer: number): number {
    switch (type) {
      case 'fire':
        // Embers: short-lived bright sparks
        return 0.3 + Math.random() * 0.6;
      case 'ice': return 0.4 + Math.random() * 1.0; // very short-lived — fast cycling blizzard
      case 'thunder': return 0.6 + Math.random() * 0.8; // animate in → hold → fade out
      case 'divine': return 1.2 + Math.random() * 1.2;
      case 'decay': return 1.5 + Math.random() * 1.8;
      case 'eldritch': return 1.0 + Math.random() * 1.4;
      case 'psychic': return 0.8 + Math.random() * 1.0;
      case 'menace': return 1.8 + Math.random() * 1.8;
      case 'fey': return 0.5 + Math.random() * 0.9;
      case 'darkness': return 3.0 + Math.random() * 2.0; // long-lived lazy wisps
      default: return 1.5 + Math.random() * 1.5;
    }
  }

  private particleSize(type: EffectName, _layer: number): number {
    switch (type) {
      case 'fire':
        // Embers: tiny bright dots
        return 1.0 + Math.random() * 2.0;
      case 'ice': return 0.5 + Math.random() * 3; // mostly small — dense blizzard particles
      case 'thunder': return 2 + Math.random() * 6; // bolt length multiplier
      case 'divine': return 2 + Math.random() * 3.5;
      case 'decay': return 2 + Math.random() * 5;
      case 'eldritch': return 2 + Math.random() * 3.5;
      case 'psychic': return 1.5 + Math.random() * 3;
      case 'menace': return 3 + Math.random() * 5;
      case 'fey': return 1.5 + Math.random() * 3;
      case 'darkness': return 4 + Math.random() * 6; // large diffuse motes
      default: return 2 + Math.random() * 3;
    }
  }

  private particleAlpha(type: EffectName, _layer: number): number {
    switch (type) {
      case 'fire':
        // Embers: bright
        return 0.7 + Math.random() * 0.3;
      case 'ice': return 0.4 + Math.random() * 0.5; // denser, more visible
      case 'thunder': return 0.8 + Math.random() * 0.2; // bright
      case 'divine': return 0.4 + Math.random() * 0.4;
      case 'decay': return 0.35 + Math.random() * 0.35;
      case 'eldritch': return 0.4 + Math.random() * 0.4;
      case 'psychic': return 0.3 + Math.random() * 0.45;
      case 'menace': return 0.25 + Math.random() * 0.4;
      case 'fey': return 0.5 + Math.random() * 0.5;
      case 'darkness': return 0.15 + Math.random() * 0.2; // subtle dark wisps over the overlay
      default: return 0.3 + Math.random() * 0.5;
    }
  }

  private particleRotSpeed(type: EffectName): number {
    switch (type) {
      case 'fire': return (Math.random() - 0.5) * 6;
      case 'ice': return (Math.random() - 0.5) * 4; // faster tumble in wind
      case 'thunder': return 0; // no spin — bolts strike outward and hold
      case 'divine': return (Math.random() - 0.5) * 2;
      case 'decay': return (Math.random() - 0.5) * 0.6;
      case 'eldritch': return (Math.random() - 0.5) * 5;
      case 'psychic': return 2.5 + Math.random() * 3.5;
      case 'menace': return (Math.random() - 0.5) * 1.2;
      case 'fey': return (Math.random() - 0.5) * 6;
      case 'darkness': return (Math.random() - 0.5) * 0.3; // barely rotating
      default: return (Math.random() - 0.5) * 2;
    }
  }

  private pickColor(colors: { primary: number; secondary: number; glow: number }, type: EffectName, _i: number, _layer: number): number {
    if (type === 'fire') {
      // Ember colors: bright warm hues
      const c = [0xffee66, 0xffcc33, 0xffaa22, 0xffffaa, 0xff8811, 0xffdd44];
      return c[Math.floor(Math.random() * c.length)];
    }
    if (type === 'thunder') {
      const c = [0xccddff, 0xffffff, 0x8899ff, 0xaabbff, 0xeeeeff, 0xddddff];
      return c[Math.floor(Math.random() * c.length)];
    }
    if (type === 'fey') {
      const c = [0xff88cc, 0xcc66ff, 0xffaadd, 0xbb55ee, 0xff66bb, 0xeebb99];
      return c[Math.floor(Math.random() * c.length)];
    }
    if (type === 'ice') {
      // Mostly white with hints of pale blue — blizzard palette
      const c = [0xffffff, 0xffffff, 0xeef4ff, 0xddeeff, 0xffffff, 0xccddee];
      return c[Math.floor(Math.random() * c.length)];
    }
    if (type === 'eldritch') {
      const c = [0x44ff44, 0x22cc22, 0x88ff66, 0x33dd33, 0xaaffaa];
      return c[Math.floor(Math.random() * c.length)];
    }
    if (type === 'decay') {
      const c = [0x66cc33, 0x88aa22, 0x446622, 0x99cc44, 0x557722];
      return c[Math.floor(Math.random() * c.length)];
    }
    if (type === 'menace') {
      const c = [0xcc0000, 0x880000, 0xaa1111, 0x660000, 0x991111];
      return c[Math.floor(Math.random() * c.length)];
    }
    if (type === 'divine') {
      const c = [0xffd700, 0xfff4c2, 0xffe066, 0xffee88, 0xffffdd];
      return c[Math.floor(Math.random() * c.length)];
    }
    if (type === 'darkness') {
      // Very dark grays — not pure black so wisps are distinguishable from the overlay
      const c = [0x0a0a12, 0x08081a, 0x101020, 0x0c0c18, 0x060610];
      return c[Math.floor(Math.random() * c.length)];
    }
    const r = Math.random();
    if (r < 0.4) return colors.primary;
    if (r < 0.7) return colors.secondary;
    return colors.glow;
  }

  private respawnParticle(p: EffectParticle, type: EffectName, origin: { x: number; y: number }, radiusPx: number, shape: string = 'circle'): void {
    const pos = this.spawnPosition(type, origin, radiusPx, p.layer, shape);
    const vel = this.spawnVelocity(type, p.layer);
    p.x = pos.x;
    p.y = pos.y;
    p.vx = vel.vx;
    p.vy = vel.vy;
    p.life = this.particleMaxLife(type, p.layer);
    p.maxLife = p.life;
    p.size = this.particleSize(type, p.layer);
    p.alpha = this.particleAlpha(type, p.layer);
    p.rotation = type === 'thunder'
      ? Math.atan2(p.y - origin.y, p.x - origin.x)
      : Math.random() * Math.PI * 2;
    p.rotSpeed = this.particleRotSpeed(type);
    p.variant = Math.floor(Math.random() * 6);
    p.seed = Math.random() * 100;
  }

  // ─── Update loop ─────────────────────────────────────────────

  private update = (): void => {
    const dt = this.ticker ? this.ticker.deltaMS / 1000 : 1 / 60;

    for (const active of this.effects.values()) {
      active.elapsed += dt;
      const radiusPx = active.effect.radiusSquares * this.gridSize;
      const type = active.effect.effect;

      // Update fire shader time
      if (active.fireShader) {
        active.fireShader.uniforms.uTime = active.elapsed;
      }
      // Update blizzard shader time
      if (active.iceShader) {
        active.iceShader.uniforms.uTime = active.elapsed;
      }
      // Update decay shader time
      if (active.decayShader) {
        active.decayShader.uniforms.uTime = active.elapsed;
      }
      // Update fey shader time
      if (active.feyShader) {
        active.feyShader.uniforms.uTime = active.elapsed;
      }
      // Update darkness shader time
      if (active.darkShader) {
        active.darkShader.uniforms.uTime = active.elapsed;
      }

      for (const p of active.particles) {
        this.updateParticleMotion(p, type, active.effect.origin, radiusPx, dt, active.elapsed);
        p.life -= dt;
        p.rotation += p.rotSpeed * dt;

        if (p.life <= 0) {
          this.respawnParticle(p, type, active.effect.origin, radiusPx, active.effect.shape);
        }
      }

      this.renderEffect(active);
    }
  };

  private updateParticleMotion(p: EffectParticle, type: EffectName, origin: { x: number; y: number }, _radiusPx: number, dt: number, elapsed: number): void {
    switch (type) {
      case 'fire': {
        // Embers: simple upward drift with gentle turbulence
        const turb = turbulence2(p.x * 0.03, p.y * 0.03, elapsed * 2, p.seed);
        p.vx += turb * 25 * dt;
        p.vy -= 15 * dt; // gentle upward pull
        p.vx *= 0.96; // damping
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        break;
      }

      case 'thunder': {
        // Bolts are stationary — they appear, animate, then fade.
        break;
      }

      case 'ice': {
        // Blizzard: driving horizontal wind with gusting surges
        const turb = turbulence2(p.x * 0.01, p.y * 0.01, elapsed * 0.8, p.seed);
        // Constant driving wind + periodic gusts
        const gustPhase = Math.sin(elapsed * 2.5 + p.seed * 5);
        const gustBoost = gustPhase > 0.7 ? gustPhase * 40 : 0; // occasional strong gusts
        p.vx += (35 + turb * 20 + gustBoost) * dt;
        // Vertical turbulence — snow swirls up and down in gusts
        p.vy += (Math.sin(elapsed * 3 + p.seed * 4) * 18 + turb * 12) * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.94; // slightly more damping for control
        p.vy *= 0.93;
        break;
      }

      case 'divine': {
        const dx = p.x - origin.x;
        const dy = p.y - origin.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const turb = turbulence2(p.x * 0.015, p.y * 0.015, elapsed, p.seed);
        p.vx += (dx / dist) * 3 * dt + turb * 6 * dt;
        p.vy += (dy / dist) * 2 * dt - 6 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.97;
        p.vy *= 0.97;
        break;
      }

      case 'decay': {
        const turb = turbulence2(p.x * 0.012, p.y * 0.012, elapsed, p.seed);
        p.vy += 10 * dt; // gravity
        p.vx += turb * 8 * dt + Math.sin(p.life * 3 + p.seed) * 4 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.97;
        break;
      }

      case 'eldritch': {
        const edx = p.x - origin.x;
        const edy = p.y - origin.y;
        const edist = Math.sqrt(edx * edx + edy * edy) || 1;
        const turb = turbulence2(p.x * 0.02, p.y * 0.02, elapsed, p.seed);
        p.vx += (-edy / edist) * 22 * dt + turb * 12 * dt;
        p.vy += (edx / edist) * 22 * dt;
        p.vx -= (edx / edist) * 6 * dt;
        p.vy -= (edy / edist) * 6 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.95;
        p.vy *= 0.95;
        break;
      }

      case 'psychic': {
        const pdx = p.x - origin.x;
        const pdy = p.y - origin.y;
        const pdist = Math.sqrt(pdx * pdx + pdy * pdy) || 1;
        const turb = turbulence2(p.x * 0.02, p.y * 0.02, elapsed, p.seed);
        p.vx += (-pdy / pdist) * 28 * dt + turb * 5 * dt;
        p.vy += (pdx / pdist) * 28 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.94;
        p.vy *= 0.94;
        break;
      }

      case 'menace': {
        const turb = turbulence2(p.x * 0.01, p.y * 0.01, elapsed, p.seed);
        p.vx += turb * 10 * dt + Math.sin(elapsed * 1.2 + p.seed) * 6 * dt;
        p.vy -= 4 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.97;
        break;
      }

      case 'fey': {
        const turb = turbulence2(p.x * 0.02, p.y * 0.02, elapsed, p.seed);
        p.vx += turb * 18 * dt + (Math.random() - 0.5) * 15 * dt;
        p.vy += (Math.random() - 0.5) * 15 * dt - 6 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.93;
        p.vy *= 0.93;
        break;
      }

      case 'darkness': {
        // Extremely slow lazy drift — dark motes barely move
        const turb = turbulence2(p.x * 0.005, p.y * 0.005, elapsed * 0.3, p.seed);
        p.vx += turb * 2 * dt;
        p.vy += Math.sin(elapsed * 0.2 + p.seed) * 0.5 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.98;
        p.vy *= 0.98;
        break;
      }

      default: {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
      }
    }
  }

  // ─── Rendering ───────────────────────────────────────────────

  private renderEffect(active: ActiveAreaEffect): void {
    const { effect, graphics, particles } = active;
    const colors = EFFECT_COLORS[effect.effect];
    const radiusPx = effect.radiusSquares * this.gridSize;
    const { x, y } = effect.origin;

    graphics.clear();

    const pulse = 0.85 + 0.15 * Math.sin(active.elapsed * 2);
    const baseAlpha = effect.alpha * pulse;

    this.drawAreaFill(graphics, effect, colors, radiusPx, x, y, baseAlpha, active.elapsed);

    // Center origin marker (skip for shader-based effects and darkness)
    if (effect.effect !== 'fire' && effect.effect !== 'ice' && effect.effect !== 'decay' && effect.effect !== 'fey' && effect.effect !== 'darkness') {
      graphics.lineStyle(0);
      graphics.beginFill(colors.glow, baseAlpha * 0.5);
      graphics.drawCircle(x, y, 4);
      graphics.endFill();
    }

    // Render themed particles
    for (const p of particles) {
      if (p.life <= 0) continue;
      const lifeRatio = p.life / p.maxLife;
      const pAlpha = lifeRatio * p.alpha;
      this.drawParticle(graphics, p, effect.effect, pAlpha, lifeRatio, active.elapsed, y, radiusPx);
    }

    // Selection highlight
    if (this.selectedEffectId === effect.id) {
      const selAlpha = 0.6 + 0.3 * Math.sin(active.elapsed * 5);
      graphics.lineStyle(3, 0xd4af37, selAlpha);
      switch (effect.shape) {
        case 'circle':
          graphics.drawCircle(x, y, radiusPx + 4);
          break;
        case 'square': {
          const half = radiusPx;
          graphics.drawRect(x - half - 4, y - half - 4, (half + 4) * 2, (half + 4) * 2);
          break;
        }
        case 'cone': {
          const coneAngle = Math.PI / 3;
          graphics.moveTo(x, y);
          graphics.arc(x, y, radiusPx + 4, -coneAngle / 2, coneAngle / 2);
          graphics.closePath();
          break;
        }
        case 'line': {
          const lineWidth = this.gridSize;
          graphics.drawRect(x - 2, y - lineWidth / 2 - 2, radiusPx + 4, lineWidth + 4);
          break;
        }
      }
    }
  }

  private drawAreaFill(
    g: PIXI.Graphics,
    effect: SpellAreaEffect,
    colors: { primary: number; secondary: number; glow: number },
    radiusPx: number,
    x: number, y: number,
    baseAlpha: number,
    elapsed: number,
  ): void {
    const type = effect.effect;

    // Shader-based effects: shader handles the main visuals — only draw a subtle border
    if (type === 'fire' || type === 'ice' || type === 'decay' || type === 'fey' || type === 'darkness') {
      const borderColor = type === 'fire' ? 0xff4400
        : type === 'ice' ? 0x88bbdd
        : type === 'fey' ? 0xcc66ff
        : type === 'darkness' ? 0x1a1030
        : 0x44aa22;
      const borderAlpha = baseAlpha * (type === 'fire' ? 0.25 : 0.3);
      g.lineStyle(1.5, borderColor, borderAlpha);
      switch (effect.shape) {
        case 'circle':
          g.drawCircle(x, y, radiusPx);
          break;
        case 'square': {
          const half = radiusPx;
          g.drawRect(x - half, y - half, half * 2, half * 2);
          break;
        }
        case 'cone': {
          const coneAngle = Math.PI / 3;
          g.moveTo(x, y);
          g.arc(x, y, radiusPx, -coneAngle / 2, coneAngle / 2);
          g.closePath();
          break;
        }
        case 'line': {
          const lineWidth = this.gridSize;
          g.drawRect(x, y - lineWidth / 2, radiusPx, lineWidth);
          break;
        }
      }
      // Ice: add wind streaks on top of shader
      if (type === 'ice') {
        for (let i = 0; i < 5; i++) {
          const sy = y - radiusPx + (i / 4) * radiusPx * 2;
          const streakAlpha = baseAlpha * (0.04 + 0.03 * Math.sin(elapsed * 3 + i * 1.7));
          const drift = Math.sin(elapsed * 2 + i * 0.9) * radiusPx * 0.15;
          g.lineStyle(0.8, 0xddeeff, streakAlpha);
          g.moveTo(x - radiusPx * 0.85 + drift, sy - radiusPx * 0.12);
          g.lineTo(x + radiusPx * 0.85 + drift, sy + radiusPx * 0.12);
        }
      }
      return;
    }

    const fillAlpha = type === 'menace' ? baseAlpha * 0.12
      : baseAlpha * 0.15;

    const borderAlpha = type === 'thunder' ? baseAlpha * 0.5
      : baseAlpha * 0.6;

    const fillColor = colors.primary;

    switch (effect.shape) {
      case 'circle': {
        g.beginFill(fillColor, fillAlpha);
        g.drawCircle(x, y, radiusPx);
        g.endFill();

        g.lineStyle(2.5, colors.primary, borderAlpha);
        g.drawCircle(x, y, radiusPx);

        break;
      }

      case 'square': {
        const half = radiusPx;
        g.beginFill(fillColor, fillAlpha);
        g.drawRect(x - half, y - half, half * 2, half * 2);
        g.endFill();
        g.lineStyle(2.5, colors.primary, borderAlpha);
        g.drawRect(x - half, y - half, half * 2, half * 2);
        break;
      }

      case 'cone': {
        const coneAngle = Math.PI / 3;
        const facing = 0;
        g.beginFill(fillColor, fillAlpha);
        g.moveTo(x, y);
        g.arc(x, y, radiusPx, facing - coneAngle / 2, facing + coneAngle / 2);
        g.closePath();
        g.endFill();
        g.lineStyle(2.5, colors.primary, borderAlpha);
        g.moveTo(x, y);
        g.arc(x, y, radiusPx, facing - coneAngle / 2, facing + coneAngle / 2);
        g.closePath();
        break;
      }

      case 'line': {
        const lineWidth = this.gridSize;
        g.beginFill(fillColor, fillAlpha);
        g.drawRect(x, y - lineWidth / 2, radiusPx, lineWidth);
        g.endFill();
        g.lineStyle(2.5, colors.primary, borderAlpha);
        g.drawRect(x, y - lineWidth / 2, radiusPx, lineWidth);
        break;
      }
    }



  }

  // ─── Per-type particle drawing ───────────────────────────────

  private drawParticle(g: PIXI.Graphics, p: EffectParticle, type: EffectName, alpha: number, lifeRatio: number, elapsed: number, _originY: number, _radiusPx: number): void {
    switch (type) {
      case 'fire': this.drawEmber(g, p, alpha, lifeRatio); break;
      case 'ice': this.drawCrystal(g, p, alpha, lifeRatio); break;
      case 'thunder': this.drawBolt(g, p, alpha, elapsed); break;
      case 'divine': this.drawRay(g, p, alpha, lifeRatio); break;
      case 'decay': this.drawDrip(g, p, alpha, lifeRatio); break;
      case 'eldritch': this.drawTendril(g, p, alpha, lifeRatio, elapsed); break;
      case 'psychic': this.drawVortex(g, p, alpha, lifeRatio); break;
      case 'menace': this.drawWisp(g, p, alpha, lifeRatio, elapsed); break;
      case 'fey': this.drawSparkle(g, p, alpha, lifeRatio); break;
      case 'darkness': this.drawDarkMote(g, p, alpha, lifeRatio); break;
      default: {
        g.beginFill(p.color, alpha);
        g.drawCircle(p.x, p.y, p.size);
        g.endFill();
      }
    }
  }

  // ─── FIRE EMBERS ────────────────────────────────────────────
  // Sparse bright sparks that fly off the shader-rendered fire field.
  // Provides secondary detail: tiny bright dots with warm glow halos.

  private drawEmber(g: PIXI.Graphics, p: EffectParticle, alpha: number, lifeRatio: number): void {
    // Quick fade-in, gradual fade-out
    const fadeMul = Math.min(1, lifeRatio * 5) * Math.min(1, (1 - lifeRatio) * 3 + 0.2);
    const a = alpha * fadeMul;
    const s = p.size * (0.6 + lifeRatio * 0.4); // shrinks as it dies

    // Bright core dot
    g.beginFill(p.color, a * 0.95);
    g.drawCircle(p.x, p.y, s * 0.5);
    g.endFill();

    // Soft warm glow halo
    g.beginFill(0xffaa33, a * 0.15);
    g.drawCircle(p.x, p.y, s * 1.8);
    g.endFill();
  }

  // ─── THUNDER ──────────────────────────────────────────────────
  // Intermittent lightning bolts that animate from origin to tip.
  // Life phases: 0-30% = bolt drawing in, 30-60% = full + bright,
  // 60-100% = fading afterimage. Bolts use deterministic segment
  // positions (seeded from p.seed) so they don't jitter each frame.

  private drawBolt(g: PIXI.Graphics, p: EffectParticle, alpha: number, _elapsed: number): void {
    const lifeRatio = p.life / p.maxLife; // 1 = just spawned, 0 = dead

    // Animation phases
    const drawInPhase = 0.7;  // bolt is still extending (lifeRatio > 0.7)
    const holdPhase = 0.35;    // bolt is fully visible (0.35 < lifeRatio < 0.7)
    // fade phase: lifeRatio < 0.35

    // How much of the bolt to reveal (0→1)
    let revealFraction: number;
    let brightness: number;

    if (lifeRatio > drawInPhase) {
      // Drawing in: bolt extends from 0% to 100%
      revealFraction = 1 - (lifeRatio - drawInPhase) / (1 - drawInPhase);
      brightness = 0.6 + revealFraction * 0.4;
    } else if (lifeRatio > holdPhase) {
      // Hold: fully visible, bright
      revealFraction = 1;
      brightness = 1.0;
    } else {
      // Fading out
      revealFraction = 1;
      brightness = lifeRatio / holdPhase;
    }

    if (brightness < 0.02) return;

    const isThunderstrike = p.variant === 0;
    const totalLen = isThunderstrike ? p.size * 14 : p.size * 5 * (0.6 + p.variant * 0.3);
    const totalSegments = isThunderstrike ? 10 : 5 + (p.variant % 4);
    const dir = p.rotation;
    const bright = alpha * brightness;

    // Deterministic jitter per segment (seeded, not random per frame)
    const segLen = totalLen / totalSegments;
    const visibleSegments = Math.ceil(totalSegments * revealFraction);

    // Build the bolt path deterministically from seed
    const points: Array<{ x: number; y: number }> = [{ x: p.x, y: p.y }];
    let cx = p.x, cy = p.y;
    // Simple seeded pseudo-random
    let rng = p.seed;
    const seededRand = () => { rng = (rng * 9301 + 49297) % 233280; return rng / 233280; };

    for (let i = 0; i < totalSegments; i++) {
      const jitter = (seededRand() - 0.5) * segLen * 1.0;
      cx += Math.cos(dir) * segLen + Math.sin(dir) * jitter;
      cy += Math.sin(dir) * segLen - Math.cos(dir) * jitter;
      points.push({ x: cx, y: cy });
    }

    // Draw visible portion of main bolt
    g.lineStyle(isThunderstrike ? 3.5 : 2, p.color, bright);
    g.moveTo(points[0].x, points[0].y);
    for (let i = 1; i <= visibleSegments && i < points.length; i++) {
      g.lineTo(points[i].x, points[i].y);
    }

    // White-hot core
    if (brightness > 0.5) {
      g.lineStyle(isThunderstrike ? 5 : 3.5, 0xffffff, bright * (isThunderstrike ? 0.35 : 0.25));
      g.moveTo(points[0].x, points[0].y);
      for (let i = 1; i <= visibleSegments && i < points.length; i++) {
        g.lineTo(points[i].x, points[i].y);
      }
    }

    // Branch fork (deterministic position)
    if (p.variant >= 2 && visibleSegments >= 3) {
      const branchIdx = 2 + Math.floor(seededRand() * (visibleSegments - 2));
      if (branchIdx < points.length) {
        const bp = points[branchIdx];
        const forkDir = dir + (seededRand() - 0.5) * 1.5;
        const forkLen = totalLen * (0.2 + seededRand() * 0.25);
        const forkSegs = 2 + Math.floor(seededRand() * 2);
        const forkReveal = Math.min(1, revealFraction * 1.3);

        g.lineStyle(1.2, p.color, bright * 0.7);
        g.moveTo(bp.x, bp.y);
        let fx = bp.x, fy = bp.y;
        const fsl = forkLen / forkSegs;
        const visFork = Math.ceil(forkSegs * forkReveal);
        for (let i = 0; i < visFork; i++) {
          const fj = (seededRand() - 0.5) * fsl * 0.8;
          fx += Math.cos(forkDir) * fsl + Math.sin(forkDir) * fj;
          fy += Math.sin(forkDir) * fsl - Math.cos(forkDir) * fj;
          g.lineTo(fx, fy);
        }
      }
    }

    // Impact glow at bolt tip during hold phase
    if (brightness > 0.8 && visibleSegments > 0) {
      const tip = points[Math.min(visibleSegments, points.length - 1)];
      g.lineStyle(0);
      g.beginFill(0xffffff, bright * 0.15);
      g.drawCircle(tip.x, tip.y, 4);
      g.endFill();
    }

    // Origin glow
    g.lineStyle(0);
    g.beginFill(p.color, bright * 0.12);
    g.drawCircle(p.x, p.y, 5);
    g.endFill();

    // Thunderstrike area flash
    if (isThunderstrike && brightness > 0.6) {
      g.beginFill(0xffffff, bright * 0.04);
      g.drawCircle(p.x, p.y, totalLen * 0.5);
      g.endFill();
    }
  }

  // ─── ICE ──────────────────────────────────────────────────────

  private drawCrystal(g: PIXI.Graphics, p: EffectParticle, alpha: number, lifeRatio: number): void {
    const s = p.size * Math.min(1, lifeRatio * 5); // very fast appear
    const fade = Math.min(1, lifeRatio * 6) * Math.min(1, (1 - lifeRatio) * 4);
    const a = alpha * fade;

    if (p.variant < 3) {
      // Snow dot — small fast-moving particle, the bulk of the blizzard
      g.lineStyle(0);
      g.beginFill(0xffffff, a * 0.8);
      g.drawCircle(p.x, p.y, s * 0.6);
      g.endFill();
      // Subtle motion trail
      const trailAngle = Math.atan2(p.vy, p.vx) + Math.PI; // behind the motion
      const trailLen = s * 1.5;
      g.lineStyle(0.5, p.color, a * 0.25);
      g.moveTo(p.x, p.y);
      g.lineTo(p.x + Math.cos(trailAngle) * trailLen, p.y + Math.sin(trailAngle) * trailLen);
    } else if (p.variant < 5) {
      // Wind streak — long horizontal blur suggesting driving snow
      const streakLen = s * 4 + 2;
      const windAngle = Math.atan2(p.vy, p.vx); // aligned to actual motion
      g.lineStyle(0.8 + s * 0.3, p.color, a * 0.6);
      g.moveTo(p.x - Math.cos(windAngle) * streakLen * 0.3, p.y - Math.sin(windAngle) * streakLen * 0.3);
      g.lineTo(p.x + Math.cos(windAngle) * streakLen, p.y + Math.sin(windAngle) * streakLen);
      // Bright leading edge
      g.lineStyle(0);
      g.beginFill(0xffffff, a * 0.5);
      g.drawCircle(p.x + Math.cos(windAngle) * streakLen, p.y + Math.sin(windAngle) * streakLen, 0.8);
      g.endFill();
    } else {
      // Snow clump — larger tumbling mass of snow
      const clumpSize = s * 1.2;
      g.lineStyle(0);
      // Soft glow halo
      g.beginFill(p.color, a * 0.12);
      g.drawCircle(p.x, p.y, clumpSize * 2.5);
      g.endFill();
      // Dense white core
      g.beginFill(0xffffff, a * 0.6);
      g.drawEllipse(p.x, p.y, clumpSize * 1.2, clumpSize * 0.8);
      g.endFill();
    }
  }

  // ─── DIVINE ───────────────────────────────────────────────────
  // Holy radiance: Latin crosses, descending light beams, wing-like sweeps.

  private drawRay(g: PIXI.Graphics, p: EffectParticle, alpha: number, lifeRatio: number): void {
    const s = p.size * Math.min(1, lifeRatio * 2);
    const fade = Math.min(1, lifeRatio * 3) * Math.min(1, (1 - lifeRatio) * 2 + 0.3);
    const a = alpha * fade;

    if (p.variant < 2) {
      // Latin cross — vertical beam longer than horizontal
      const vArm = s * 2.0;   // full vertical extent
      const hArm = s * 1.2;   // horizontal half-width
      const thick = s * 0.25;
      const crossbar = -vArm * 0.25; // crossbar sits in upper third

      g.beginFill(p.color, a);
      // Vertical bar
      g.drawRect(p.x - thick, p.y - vArm * 0.6, thick * 2, vArm * 1.2);
      // Horizontal bar (slightly above center)
      g.drawRect(p.x - hArm, p.y + crossbar - thick, hArm * 2, thick * 2);
      g.endFill();

      // Bright center intersection glow
      g.beginFill(0xffffff, a * 0.4);
      g.drawCircle(p.x, p.y + crossbar, thick * 2.5);
      g.endFill();
    } else if (p.variant < 4) {
      // Descending light beam — tall narrow column of light tapering down
      const beamH = s * 3.5;
      const topW = s * 0.15;
      const botW = s * 0.8;
      const shimmer = Math.sin(p.seed * 10 + p.life * 6) * 0.15;

      g.beginFill(p.color, a * (0.6 + shimmer));
      g.moveTo(p.x - topW, p.y - beamH * 0.7);
      g.lineTo(p.x + topW, p.y - beamH * 0.7);
      g.lineTo(p.x + botW, p.y + beamH * 0.3);
      g.lineTo(p.x - botW, p.y + beamH * 0.3);
      g.closePath();
      g.endFill();

      // Bright core line
      g.lineStyle(1, 0xffffff, a * 0.3);
      g.moveTo(p.x, p.y - beamH * 0.7);
      g.lineTo(p.x, p.y + beamH * 0.3);
    } else {
      // Wing sweep — curved arc suggesting angel wings
      const wingSpan = s * 2.5;
      const sweep = Math.PI * 0.35;
      const baseAngle = -Math.PI / 2 + p.rotation * 0.1;

      // Left wing
      g.lineStyle(1.5, p.color, a * 0.8);
      g.arc(p.x + wingSpan * 0.3, p.y, wingSpan, baseAngle + Math.PI - sweep, baseAngle + Math.PI + sweep * 0.2);
      // Right wing
      g.arc(p.x - wingSpan * 0.3, p.y, wingSpan, baseAngle - sweep * 0.2, baseAngle + sweep);

      // Feather tips — small lines off the wing arcs
      for (let f = 0; f < 3; f++) {
        const fa = baseAngle + Math.PI - sweep + (f / 3) * sweep * 1.2;
        const fx = p.x + wingSpan * 0.3 + Math.cos(fa) * wingSpan;
        const fy = p.y + Math.sin(fa) * wingSpan;
        g.lineStyle(0.8, p.color, a * 0.4);
        g.moveTo(fx, fy);
        g.lineTo(fx + Math.cos(fa - 0.3) * s * 0.8, fy + Math.sin(fa - 0.3) * s * 0.8);
      }
    }

    // Warm golden radiance glow (all variants)
    g.lineStyle(0);
    g.beginFill(0xffd700, a * 0.08);
    g.drawCircle(p.x, p.y, s * 1.5);
    g.endFill();
  }

  // ─── DECAY ────────────────────────────────────────────────────

  private drawDrip(g: PIXI.Graphics, p: EffectParticle, alpha: number, lifeRatio: number): void {
    const s = p.size * Math.min(1, lifeRatio * 2);
    const elongation = 1 + (1 - lifeRatio) * 1.4;

    g.beginFill(p.color, alpha);
    g.moveTo(p.x, p.y + s * elongation);
    g.bezierCurveTo(
      p.x - s * 0.8, p.y + s * 0.3,
      p.x - s * 0.9, p.y - s * 0.5,
      p.x, p.y - s * 0.6,
    );
    g.bezierCurveTo(
      p.x + s * 0.9, p.y - s * 0.5,
      p.x + s * 0.8, p.y + s * 0.3,
      p.x, p.y + s * elongation,
    );
    g.endFill();

    if (p.variant < 2) {
      g.beginFill(0x88ff44, alpha * 0.15);
      g.drawCircle(p.x, p.y - s * 0.2, s * 0.3);
      g.endFill();
    }
    // Drip trail
    if (p.variant >= 3 && lifeRatio < 0.6) {
      g.lineStyle(0.8, p.color, alpha * 0.3);
      g.moveTo(p.x, p.y - s * 0.6);
      g.lineTo(p.x + (Math.random() - 0.5) * 2, p.y - s * 1.5);
    }
  }

  // ─── ELDRITCH ─────────────────────────────────────────────────

  private drawTendril(g: PIXI.Graphics, p: EffectParticle, alpha: number, lifeRatio: number, elapsed: number): void {
    const len = p.size * 3.5 * Math.min(1, lifeRatio * 2);
    const wiggle = Math.sin(elapsed * 4 + p.seed * 2) * len * 0.5;
    const wiggle2 = Math.cos(elapsed * 3 + p.seed * 1.3) * len * 0.3;

    // Thicker tendrils with more curve points
    g.lineStyle(1.8, p.color, alpha);
    g.moveTo(p.x, p.y);
    g.bezierCurveTo(
      p.x + wiggle, p.y - len * 0.3,
      p.x - wiggle2, p.y - len * 0.65,
      p.x + wiggle * 0.4, p.y - len,
    );

    // Glowing tip
    g.lineStyle(0);
    g.beginFill(p.color, alpha * 0.7);
    g.drawCircle(p.x + wiggle * 0.4, p.y - len, 2);
    g.endFill();

    // Root glow
    g.beginFill(p.color, alpha * 0.2);
    g.drawCircle(p.x, p.y, 2.5);
    g.endFill();
  }

  // ─── PSYCHIC ──────────────────────────────────────────────────

  private drawVortex(g: PIXI.Graphics, p: EffectParticle, alpha: number, lifeRatio: number): void {
    const s = p.size * Math.min(1, lifeRatio * 2);
    const arcLen = Math.PI * (0.3 + p.variant * 0.15);

    g.lineStyle(1.8, p.color, alpha);
    g.arc(p.x, p.y, s * 2.2, p.rotation, p.rotation + arcLen);

    // Second inner arc
    if (p.variant >= 2) {
      g.lineStyle(1, p.color, alpha * 0.5);
      g.arc(p.x, p.y, s * 1.2, p.rotation + Math.PI * 0.3, p.rotation + Math.PI * 0.3 + arcLen * 0.7);
    }

    g.lineStyle(0);
    g.beginFill(p.color, alpha * 0.4);
    g.drawCircle(p.x, p.y, s * 0.45);
    g.endFill();
  }

  // ─── MENACE ───────────────────────────────────────────────────

  private drawWisp(g: PIXI.Graphics, p: EffectParticle, alpha: number, lifeRatio: number, elapsed: number): void {
    const s = p.size * Math.min(1, lifeRatio * 2);
    const len = s * 2.8;
    const sway = Math.sin(elapsed * 1.5 + p.seed) * s * 0.4;

    g.lineStyle(0);
    g.beginFill(p.color, alpha * 0.5);
    g.drawEllipse(p.x + sway, p.y, s * 0.45, len);
    g.endFill();

    // Trailing shadow
    g.beginFill(p.color, alpha * 0.15);
    g.drawEllipse(p.x - sway * 0.5, p.y + len * 0.3, s * 0.3, len * 0.5);
    g.endFill();

    // Dark core
    if (p.variant < 3) {
      g.beginFill(0x220000, alpha * 0.3);
      g.drawCircle(p.x, p.y - len * 0.2, s * 0.2);
      g.endFill();
    }
  }

  // ─── FEY ──────────────────────────────────────────────────────

  private drawSparkle(g: PIXI.Graphics, p: EffectParticle, alpha: number, lifeRatio: number): void {
    const s = p.size * Math.min(1, lifeRatio * 3);
    const fade = Math.min(1, lifeRatio * 4) * Math.min(1, (1 - lifeRatio) * 2.5);
    const a = alpha * fade;

    // Soft glow halo (all particles — creates depth atmosphere)
    g.lineStyle(0);
    g.beginFill(p.color, a * 0.06);
    g.drawCircle(p.x, p.y, s * 3.0);
    g.endFill();

    if (p.layer === 0) {
      // Inner glow orbs — larger, brighter, warm light pools
      g.beginFill(0xffffff, a * 0.3);
      g.drawCircle(p.x, p.y, s * 1.5);
      g.endFill();
      g.beginFill(p.color, a * 0.5);
      g.drawCircle(p.x, p.y, s * 0.9);
      g.endFill();
      g.beginFill(0xffffff, a * 0.2);
      g.drawCircle(p.x, p.y, s * 0.3);
      g.endFill();
    } else if (p.layer === 2) {
      // Outer trailing wisps — dim, elongated
      const trail = s * 2;
      g.beginFill(p.color, a * 0.2);
      g.drawEllipse(p.x, p.y, s * 0.4, trail);
      g.endFill();
      g.beginFill(0xffffff, a * 0.1);
      g.drawCircle(p.x, p.y - trail * 0.3, s * 0.2);
      g.endFill();
    } else {
      // Mid sparkle stars — keep the star shape
      const points = 4 + (p.variant % 4);
      g.beginFill(p.color, a);
      for (let i = 0; i <= points * 2; i++) {
        const starAngle = p.rotation + (i / (points * 2)) * Math.PI * 2;
        const r = (i % 2 === 0) ? s * 1.6 : s * 0.25;
        const px = p.x + Math.cos(starAngle) * r;
        const py = p.y + Math.sin(starAngle) * r;
        if (i === 0) g.moveTo(px, py); else g.lineTo(px, py);
      }
      g.endFill();

      // Glitter core
      g.beginFill(0xffffff, a * 0.5);
      g.drawCircle(p.x, p.y, s * 0.3);
      g.endFill();
    }
  }

  // ─── DARKNESS MOTES ──────────────────────────────────────────
  // Subtle dark wisps that drift over the heavy overlay.
  // Barely visible — they add slight texture/movement to the void.

  private drawDarkMote(g: PIXI.Graphics, p: EffectParticle, alpha: number, lifeRatio: number): void {
    const fade = Math.min(1, lifeRatio * 3) * Math.min(1, (1 - lifeRatio) * 2);
    const a = alpha * fade;
    const s = p.size * (0.8 + lifeRatio * 0.2);

    // Soft dark blob
    g.lineStyle(0);
    g.beginFill(p.color, a * 0.4);
    g.drawEllipse(p.x, p.y, s * 1.2, s * 0.8);
    g.endFill();

    // Darker core
    g.beginFill(0x020208, a * 0.3);
    g.drawCircle(p.x, p.y, s * 0.4);
    g.endFill();
  }

  // ─── Cleanup ─────────────────────────────────────────────────

  destroy(): void {
    if (this.ticker) {
      this.ticker.remove(this.update, this);
      this.ticker = null;
    }
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    for (const active of this.effects.values()) {
      if (active.fireMesh) {
        active.fireMesh.destroy();
      }
      if (active.iceMesh) {
        active.iceMesh.destroy();
      }
      if (active.decayMesh) {
        active.decayMesh.destroy();
      }
      if (active.feyMesh) {
        active.feyMesh.destroy();
      }
      if (active.darkMesh) {
        active.darkMesh.destroy();
      }
      active.container.destroy({ children: true });
    }
    this.effects.clear();
    this.container.destroy({ children: true });
  }
}
